/**
 * WorkstreamPage — /workstream/:id (plan Task 11.4, spec §3.2/§3.3/§3.5/§6.2/§8.1/§8.4).
 *
 * Four surfaces in one page, in lifecycle order:
 *   1. plan review + edit (owner) while `awaiting_approval`;
 *   2. approval, sending BOTH `version` and `content_hash`;
 *   3. live progress over SSE (`useWorkstreamStream`);
 *   4. the staged report once a plan version has been approved.
 *
 * ── Rendering rules that are product decisions, not styling ────────────────
 *
 * **No tool name appears as VISIBLE TEXT.** Spec §3.2: a user reads product
 * vocabulary, never a dispatch identifier. Everything rendered comes from
 * domain-language fields (`goal`, `query`, `success_criterion`, `scope`,
 * `searched_corpora`) or from a catalog `label`.
 *
 * The plan EDITOR is where that rule earns its precision (user decision
 * 2026-08-31). An owner picks a step's corpora and their tools from
 * dropdowns, so the codes now exist on this page — as `<option value>` and as
 * React keys, which `textContent` never surfaces. Every chip, option and
 * heading renders `catalogLabel(...)`. If you find yourself printing a raw
 * `corpus`/`tool` code into a text node, that is the regression.
 *
 * **Four things that all look like "a step with nothing in it" must read
 * DIFFERENTLY**, because collapsing any of them into a generic empty state is
 * spec §8.1's silent-absence failure wearing a different hat:
 *   1. UNPROBED SCOPE is labelled *unvalidated* — never "validated" and never
 *      omitted (§3.3: the unprobed remainder is explicitly marked, not
 *      silently treated as validated).
 *   2. A SKIPPED STEP shows its `skip_reason` (§8.4: "never silently absent").
 *   3. A CARRIED-FORWARD step says which plan version it came from.
 *   4. Every evidence reference shows its RETRIEVAL TIMESTAMP (§6.2: resumed
 *      work reuses persisted results, so staleness is disclosed, not hidden).
 *
 * **`searched_corpora` is §8.1's absence scope.** An empty array means
 * "searched, found nothing"; an absent one means no scope was recorded. These
 * are different claims and are rendered as different sentences.
 */
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import { useWorkstreamStream } from '../hooks/useWorkstreamStream';
import { parseEvidencePath, resolveEvidenceTarget } from './AgentEvidencePage';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';
import { PlanningIndicator } from '../components/workstream/PlanningIndicator';
import { StepRail, displayStatusOf } from '../components/workstream/StepRail';
import { PlanRail } from '../components/workstream/PlanRail';
import { AnalysisSetup } from '../components/workstream/AnalysisSetup';
import type {
  AnalysisCoverage,
  AnalysisPhase,
  ApprovePlanResponse,
  CandidateReference,
  ComputedSlotValue,
  ContractAnswerEnvelope,
  ContractReportProjection,
  EvidenceCitation,
  ExcerptCitation,
  CapabilityCatalog,
  PlanContent,
  PlanRequirement,
  PlanStep,
  PlanVersionResponse,
  ProjectionAppendix,
  ProjectionPercentage,
  ProjectionTable,
  ReferenceEntry,
  ReportProjection,
  ReportTemplateOption,
  StepSummary,
  StepToolCallsResponse,
  SynthesisContent,
  TemplatedBlock,
  TemplatedBodySection,
  TemplatedReportBody,
  TemplatedSlot,
  TemplatedSubject,
  ToolCallAudit,
  WorkstreamDetail,
  WorkstreamReport,
} from '../types/workstream';
import { TERMINAL_WORKSTREAM_STATUSES } from '../types/workstream';
import { safeCompetitiveIntelUrl } from '../utils/competitiveIntelUrl';
import { openInAppLink } from '../utils/openInNewTab';
import { findInTab, useTab } from '../shell/TabContext';
import { demoApiResponse } from '../demo/cepiDemoReplay';

const API_URL = import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

// ---------------------------------------------------------------------------
// Fetch helpers (BFF base URL + bearer token, mirroring services/authApi.ts)
// ---------------------------------------------------------------------------

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await auth.currentUser?.getIdToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  Object.assign(headers, getImpersonationHeaders());
  return headers;
}

export async function apiGet<T>(path: string): Promise<{ ok: boolean; status: number; body: T | null }> {
  const demo = await demoApiResponse<T>('GET', path);
  if (demo) return demo;
  // The `fetch` itself is inside the try, not just the JSON parse. A rejected
  // fetch (offline, DNS, a dropped connection) used to propagate out of here;
  // with the catalog's loading gate that left the source editor stuck on
  // "Loading…" forever, because neither `catalog` nor `catalogFailed` was ever
  // set. `status: 0` is "no response happened", distinct from any real code.
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { headers: await authHeaders() });
  } catch {
    return { ok: false, status: 0, body: null };
  }
  let body: T | null = null;
  try {
    body = (await res.json()) as T;
  } catch {
    body = null;
  }
  return { ok: res.ok, status: res.status, body };
}

async function apiPost<T>(
  path: string,
  payload: unknown,
): Promise<{ ok: boolean; status: number; body: T | null }> {
  const demo = await demoApiResponse<T>('POST', path, payload);
  if (demo) return demo;
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  let body: T | null = null;
  try {
    body = (await res.json()) as T;
  } catch {
    body = null;
  }
  return { ok: res.ok, status: res.status, body };
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  drafting: 'Drafting the plan',
  awaiting_approval: 'Awaiting your approval',
  queued: 'Queued',
  running: 'Running',
  complete: 'Complete',
  complete_with_flags: 'Complete with flags',
  failed: 'Failed',
  cancelled: 'Cancelled',
  infeasible: 'Not feasible',
};

const STEP_STATUS_LABEL: Record<string, string> = {
  pending: 'Not started',
  running: 'In progress',
  done: 'Done',
  flagged: 'Done, with flags',
  failed: 'Failed',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
};

function statusLabel(status: string, synthesisStatus?: string | null): string {
  if (synthesisStatus === 'unavailable' && (status === 'complete' || status === 'complete_with_flags')) {
    return `Execution completed${status === 'complete_with_flags' ? ' with flags' : ''} · Report unavailable`;
  }
  return STATUS_LABEL[status] ?? status;
}

function stepStatusLabel(status: string): string {
  return STEP_STATUS_LABEL[status] ?? status;
}

function stepStatusBadge(status: string): string {
  if (status === 'done') return 'badge-success';
  if (status === 'running') return 'badge-info';
  if (status === 'flagged') return 'badge-warning';
  if (status === 'failed') return 'badge-danger';
  return 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600';
}

/** ISO → a readable absolute timestamp. Absolute, never "3 minutes ago":
 *  spec §6.2 wants *when a fact was observed*, which a relative phrase loses. */
function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return 'unknown time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Render a step's scope in domain language. `approval_refs` and
 * `unresolved_named_entities` are machinery, not user copy, so they are not
 * echoed here as raw scope keys — `unresolved_named_entities` gets its own
 * explicit unvalidated treatment in the plan panel instead.
 */
const NON_RENDERED_SCOPE_KEYS = new Set([
  'approval_refs', 'unresolved_named_entities', 'document_types', 'version_anchor', 'cogni_queries',
]);

/**
 * A paired step's companion Cogni queries (spec §4.1 `scope.cogni_queries`,
 * §4.8). Excluded from the generic `scopeEntries` join above — a comma-joined
 * natural-language query would be ambiguous with the query's own commas — and
 * rendered on its own line instead, both on the read surface and the editor.
 */
function cogniQueries(scope: Record<string, unknown> | undefined): string[] {
  const value = scope?.['cogni_queries'];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function scopeEntries(
  scope: Record<string, unknown> | undefined,
  haDocumentTypes: { value: string; label: string }[] | undefined = undefined,
): [string, string][] {
  if (!scope) return [];
  const out: [string, string][] = [];
  for (const [key, value] of Object.entries(scope)) {
    if (NON_RENDERED_SCOPE_KEYS.has(key)) continue;
    if (value === null || value === undefined) continue;
    const text = Array.isArray(value) ? value.map(String).join(', ') : String(value);
    if (!text) continue;
    // §7.4: `report_bindings` sits beside `input_from_steps` in the same
    // vocabulary, and the two mean opposite things — one is what gets
    // ACQUIRED, the other is only what the report may CITE. Rendered as the
    // bare key they read as synonyms on the approval surface.
    out.push([key === 'report_bindings' ? 'report evidence (cited, not acquired)' : key.replace(/_/g, ' '), text]);
  }
  const selected = scope['document_types'];
  if (Array.isArray(selected)) {
    const labels = selected
      .map((value) => haDocumentTypes?.find((option) => option.value === value)?.label)
      .filter((label): label is string => Boolean(label));
    if (labels.length > 0) out.push(['document types', labels.join(', ')]);
  }
  if (scope['version_anchor'] === 'current' || scope['version_anchor'] === 'origin') {
    out.push([
      'label version',
      scope['version_anchor'] === 'origin' ? 'Original' : 'Current',
    ]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Source editing — a step's corpora and, per corpus, its tools
// ---------------------------------------------------------------------------

/**
 * One corpus and the tools chosen for it. The wire format is a FLAT list of
 * `(corpus, tool)` pairs; the editor groups it because that is the shape a
 * reviewer edits in ("which documents, and what will you do to them") and
 * because a grouped row can exist with ZERO tools while the user is still
 * choosing. A flat-pairs draft would make such a row vanish mid-edit.
 */
interface SourceRow {
  corpus: string;
  tools: string[];
}

function toSourceRows(requirements: PlanRequirement[] | undefined): SourceRow[] {
  const rows: SourceRow[] = [];
  for (const req of requirements ?? []) {
    if (!req || typeof req.corpus !== 'string' || typeof req.tool !== 'string') continue;
    let row = rows.find((r) => r.corpus === req.corpus);
    if (!row) {
      row = { corpus: req.corpus, tools: [] };
      rows.push(row);
    }
    if (!row.tools.includes(req.tool)) row.tools.push(req.tool);
  }
  return rows;
}

/** Back to flat pairs. A row with no tools contributes nothing — selecting a
 *  corpus without saying what to do with it grants no capability. */
function fromSourceRows(rows: SourceRow[]): PlanRequirement[] {
  return rows.flatMap((row) => row.tools.map((tool) => ({ corpus: row.corpus, tool })));
}

/**
 * A code's human label. Falls back to the raw code ONLY when the catalog does
 * not know it — a plan version naming a corpus that has since left the matrix
 * must still be visible to the person deciding what to do about it. Rendering
 * nothing there would silently drop a source from a plan under review.
 *
 * This fallback is the ONE place a raw code can become visible text, and it is
 * a deliberate, reviewed exception to that rule (flagged by review 2026-08-31;
 * user decision: keep it). By the time it fires, the code is the only
 * identifying information left, and a reviewer who cannot see WHICH source is
 * unrecognised can only remove it blind. Do not "fix" this into a neutral
 * placeholder without asking again.
 *
 * It cannot fire while the catalog is LOADING — the caller withholds the whole
 * editor until `catalog !== null`, since otherwise every known code would take
 * this path for the duration of the fetch.
 */
function catalogLabel(options: { code: string; label: string }[] | undefined, code: string): string {
  return options?.find((o) => o.code === code)?.label ?? code;
}

// ---------------------------------------------------------------------------
// Evidence (spec §6.2 retrieval timestamp, §3.4 binding time)
// ---------------------------------------------------------------------------

/**
 * A DOCUMENT reference: a passage in a real document, which is the only thing
 * `CitationRow` may ever render. Both shapes that reach it are that — a legacy
 * claim's `EvidenceCitation` and a templated body's `ExcerptCitation` (§11.5's
 * first reference kind) differ only in how they are keyed. §11.5's SECOND kind,
 * a computed-slot reference, is NOT one of these and has its own renderer:
 * see `ComputedReferenceRow`.
 */
type DocumentCitation = EvidenceCitation | ExcerptCitation;

/** What a citation points AT: its source, and the section within it. */
function citationLabel(citation: DocumentCitation): string {
  const section = citation.section_number ? ` · section ${citation.section_number}` : '';
  const drug = citation.drug_name?.trim();
  return `${drug ? `${drug} · ` : ''}${citation.corpus}${section}`;
}

function CitationRow({ citation }: { citation: DocumentCitation }) {
  const href = citation.citation_alignment === 'chunk_only'
    ? regulatoryCitationHref(citation) ?? citation.deep_link
    : citation.deep_link;
  return (
    <li className="border-l-2 border-slate-200 pl-3 py-1" data-testid="evidence-citation">
      {/* The citation's LABEL is what it points at — the source and the
          section. `anchor_display` used to be the link text, and for every
          corpus without a version column (sba, eu, epar, fda_letter — all but
          us_label) it rendered the literal string "changed since observed" on
          EVERY citation, unconditionally, with no comparison performed. It
          read as an assertion that the document had changed; it was only ever
          saying the anchor cannot express a version. Worse, the value behind
          it is `updated_at`, OUR ingestion timestamp, so it moved on a
          re-ingest of a byte-identical document. Removed 2026-08-31 (user
          decision): a caveat that fires on all 39 citations in a report tells
          a reader nothing and misstates what it knows. */}
      <div className="text-sm text-slate-800">
        {href ? (
          <a
            href={href}
            onClick={(e) => openInAppLink(e, href)}
            className="text-indigo-700 underline"
            target="_blank"
            rel="noreferrer"
          >
            {citationLabel(citation)}
          </a>
        ) : (
          <span>{citationLabel(citation)}</span>
        )}
      </div>
      {citation.quoted_span && (
        // Rule 27: a quoted span from a source document is visually distinct
        // from model prose — a 2px left rule, italic serif and a background
        // token of its own (declared values, plan Task 8).
        <blockquote className="border-l-2 border-slate-300 bg-slate-50 pl-2 py-0.5 text-sm italic font-serif text-slate-700 mt-1">
          “{citation.quoted_span}”
        </blockquote>
      )}
      {citation.citation_alignment === 'chunk_only' && (
        <p className="mt-1 text-xs text-amber-800" data-testid="citation-alignment-warning">
          Exact page highlight unavailable
        </p>
      )}
      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
        {/* §6.2: every reference discloses WHEN it was observed. */}
        <span data-testid="evidence-retrieved-at">
          Retrieved {formatTimestamp(citation.retrieved_at)}
        </span>
        {citation.data_as_of && <span>Data as of {formatTimestamp(citation.data_as_of)}</span>}
        {/* §3.4: bound at approval vs bound at runtime are different claims. */}
        <span data-testid="evidence-binding-time">
          Bound at {citation.binding_time === 'approval' ? 'approval' : 'runtime'}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Plan review + approval (spec §3.3)
// ---------------------------------------------------------------------------

/**
 * The unprobed-scope banner. §3.3: where an edited plan's new scope exceeds the
 * bounded probe budget, the excess is sampled and *the unprobed remainder is
 * explicitly marked as unvalidated*. It is NOT silently treated as validated,
 * and it is NOT a reason to reject the plan.
 */
function UnprobedScopeNotice({ plan }: { plan: PlanVersionResponse }) {
  const entities = plan.unprobed_scope?.entities ?? [];
  const tier2Ran = plan.unprobed_scope?.tier2_ran === true;

  if (!tier2Ran) {
    return (
      <div
        className="rounded-md border border-amber-300 bg-amber-50 p-3"
        data-testid="unprobed-scope-notice"
      >
        <p className="text-sm font-medium text-amber-900">
          This plan version is UNVALIDATED
        </p>
        <p className="text-sm text-amber-800 mt-1">
          The feasibility check has not run against this version, so nothing in its
          scope has been confirmed to exist. It cannot be approved until it has.
        </p>
      </div>
    );
  }
  if (entities.length === 0) return null;
  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-3"
      data-testid="unprobed-scope-notice"
    >
      <p className="text-sm font-medium text-amber-900">
        Part of this plan&apos;s scope is UNVALIDATED
      </p>
      <p className="text-sm text-amber-800 mt-1">
        The feasibility check is bounded, so the following was not checked and is
        explicitly <strong>not</strong> confirmed to exist. It has not been treated as
        validated:
      </p>
      <ul className="mt-2 space-y-0.5">
        {entities.map((e, i) => (
          <li key={`${e.key}-${e.value}-${i}`} className="text-sm text-amber-900">
            <span className="font-medium">{e.key.replace(/_/g, ' ')}:</span> {e.value}{' '}
            <span className="italic">— unvalidated</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CapabilityViolations({ plan }: { plan: PlanVersionResponse }) {
  const violations = plan.capability_validation?.violations ?? [];
  if (violations.length === 0) return null;
  return (
    <div
      className="rounded-md border border-red-300 bg-red-50 p-3"
      data-testid="capability-violations"
    >
      <p className="text-sm font-medium text-red-900">
        This plan cannot be approved as written
      </p>
      <ul className="mt-2 list-disc pl-5 space-y-0.5">
        {violations.map((v, i) => (
          <li key={i} className="text-sm text-red-900">{v}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A step as the editor holds it: the plan step plus its sources GROUPED by
 * corpus. `sourceRows` is editor-only state and never crosses the wire —
 * `fromDraft` flattens it back to `requirements`.
 */
type DraftStep = PlanStep & { sourceRows: SourceRow[] };

function toDraft(steps: PlanStep[] | undefined): DraftStep[] {
  return (steps ?? []).map((step) => ({
    ...step,
    query: step.query ?? '',
    sourceRows: toSourceRows(step.requirements),
  }));
}

/**
 * Back to a wire-shaped step. `sources` is DROPPED, not carried: it is the
 * server's rendering of `requirements`, so echoing the old sentence back
 * alongside newly-edited requirements would persist a contradiction into an
 * immutable, content-hashed plan version. The API re-renders it on read.
 */
function fromDraft(step: DraftStep): PlanStep {
  const { sourceRows, sources: _sources, ...rest } = step;
  void _sources;
  // Blank Cogni-query boxes are draft-only scaffolding (an eligible step
  // always shows at least one, even before anything is typed into it) — drop
  // them on save, and drop the key entirely once nothing nonblank remains.
  // Only touched when the key is actually present; every other step's scope
  // passes through untouched.
  if (rest.scope && 'cogni_queries' in rest.scope) {
    const raw = rest.scope['cogni_queries'];
    const kept = Array.isArray(raw)
      ? raw.filter((q): q is string => typeof q === 'string' && q.trim() !== '')
      : [];
    const scope = { ...rest.scope };
    if (kept.length > 0) scope['cogni_queries'] = kept;
    else delete scope['cogni_queries'];
    return { ...rest, scope, requirements: fromSourceRows(sourceRows) };
  }
  return { ...rest, requirements: fromSourceRows(sourceRows) };
}

function AnalysisLimits({ budget }: { budget: PlanContent['analysis_budget'] }) {
  if (!budget) return null;
  return <p className="mt-2 text-xs text-slate-600" data-testid="analysis-budget">
    Whole-run limits: {budget.cogni_search} Cogni calls · {budget.investigator} Investigator calls · {budget.read_file} document reads · Keyword has no separate call cap.
    {' '}Analysis: {budget.acquisition} acquisition turns · {budget.map} analysis batches · {budget.final} report-stage request{budget.final === 1 ? '' : 's'} · {budget.context_tokens.toLocaleString('en-US')} context tokens · {budget.output_tokens.toLocaleString('en-US')} output tokens per request.
    {' '}Only approved sources and tools may be used. These limits do not guarantee a dollar cost.
  </p>;
}

/**
 * The RESOLVED report template — read-only on every surface (spec §6.2).
 *
 * This pair, not the id alone, is what an approval covers: the server pins
 * `(template_id, version)` into plan content, `approved_plan_hash` hashes it,
 * and a later registry revision therefore cannot change what an approved plan
 * means. It is rendered on the review surface too because §6.2 requires the
 * choice to be visible BEFORE approval, and the review surface is where the
 * approval happens.
 *
 * `templates` is only a source of human LABELS; the pinned version is printed
 * from the pin itself, never from the catalog row, so a catalog offering a
 * newer version of the same id cannot misreport what is pinned.
 */
function ReportTemplatePin({
  pin,
  templates,
}: {
  pin: PlanContent['report_template'];
  templates: ReportTemplateOption[] | undefined;
}) {
  if (!pin) return null;
  const label = templates?.find((t) => t.template_id === pin.template_id)?.label ?? pin.template_id;
  return (
    <p className="mt-2 text-xs text-slate-600" data-testid="report-template-pin">
      <span className="font-medium">Report template:</span> {label} · version {pin.version}
    </p>
  );
}


/**
 * The report half of the analysis step's editor: which template writes the
 * report, and which earlier steps it may cite.
 *
 * Two things here are the point of the whole control, not styling:
 *
 *   1. **The options come from the catalog.** Spec §6.1 makes the server
 *      registry the only source of template ids; a list hardcoded here would
 *      offer a retired template, or fail to offer a new one, with nothing to
 *      catch it. Absent a catalog (still loading, or its fetch failed) NO
 *      chooser is rendered — the pinned template above still is.
 *   2. **A binding is not an acquisition edge.** `report_bindings` is offered
 *      over EVERY earlier step, independently of `input_from_steps`, and
 *      writing one never touches `input_from_steps` (§7.4, round-2 P2-B):
 *      `input_from_steps` is what the executor unions into the acquired
 *      cohort, so binding through it would restore candidates a later
 *      filtering step deliberately removed — wrong drugs in the report rather
 *      than an error.
 */
function ReportPlanEditor({
  stepIndex,
  scope,
  earlierSteps,
  templates,
  selectedTemplateId,
  onSelectTemplate,
  onChangeBindings,
}: {
  stepIndex: number;
  scope: Record<string, unknown>;
  earlierSteps: DraftStep[];
  templates: ReportTemplateOption[] | undefined;
  selectedTemplateId: string;
  onSelectTemplate: (templateId: string) => void;
  onChangeBindings: (bindings: number[]) => void;
}) {
  const bindings = Array.isArray(scope['report_bindings']) ? (scope['report_bindings'] as number[]) : [];
  return (
    <fieldset className="text-sm space-y-1" data-testid="report-plan-editor">
      <legend className="text-xs text-slate-600">Report</legend>
      {templates && templates.length > 0 && (
        <label className="block text-xs text-slate-600">
          Report template
          <select
            aria-label={`Step ${stepIndex + 1} report template`}
            className="mt-1 block max-w-full border border-slate-300 rounded px-2 py-1 text-sm"
            value={selectedTemplateId}
            onChange={(event) => onSelectTemplate(event.target.value)}
          >
            {templates.map((template) => (
              <option key={`${template.template_id}:${template.version}`} value={template.template_id}>
                {template.label}
              </option>
            ))}
          </select>
          <span className="mt-0.5 block text-slate-500">
            Saving pins this template at its current version; the approval covers that pair.
          </span>
        </label>
      )}
      <p className="text-xs text-slate-600">Report evidence (documents the report may cite)</p>
      {earlierSteps.map((earlier, earlierIdx) => (
        <label key={earlierIdx} className="block">
          <input
            type="checkbox"
            aria-label={`Report evidence step ${earlierIdx + 1}`}
            checked={bindings.includes(earlierIdx + 1)}
            onChange={(event) =>
              onChangeBindings(
                event.target.checked
                  ? [...bindings, earlierIdx + 1].sort((a, b) => a - b)
                  : bindings.filter((value) => value !== earlierIdx + 1),
              )
            }
          />{' '}
          {earlierIdx + 1}. {earlier.goal}
        </label>
      ))}
      <p className="text-xs text-slate-500">
        Evidence chosen here does not change which drugs are acquired — the input cohorts
        above decide that. Choosing none keeps the report on the final input cohort alone.
      </p>
    </fieldset>
  );
}

/**
 * A step's sources: one row per corpus, each carrying its own tool picks.
 *
 * Options come from the server's capability catalog, and a row's tool list is
 * narrowed to `matrix[corpus]` — the tools that corpus actually supports — so
 * a pair tier-1 validation would reject cannot be composed here. That is a
 * convenience, NOT the guarantee: `validate_plan_capabilities` still runs on
 * every version and is what actually blocks approval.
 */
function SourceEditor({
  rows,
  catalog,
  stepLabel,
  onChange,
  originalLabels = false,
}: {
  rows: SourceRow[];
  catalog: CapabilityCatalog | null;
  stepLabel: string;
  onChange: (rows: SourceRow[]) => void;
  originalLabels?: boolean;
}) {
  const unusedCorpora = (catalog?.corpora ?? []).filter(
    (c) => !rows.some((r) => r.corpus === c.code),
  );

  return (
    <div className="space-y-2" data-testid="source-editor">
      <span className="block text-xs text-slate-600">Sources</span>
      {rows.length === 0 && (
        <p className="text-xs text-slate-500" data-testid="no-sources-note">
          No sources chosen. Leave this empty and we will work them out from the
          goal and query; choose them here to decide yourself.
        </p>
      )}
      {rows.map((row, rowIdx) => {
        const corpusLabel = catalogLabel(catalog?.corpora, row.corpus);
        const available = (catalog?.matrix?.[row.corpus] ?? []).filter(
          (t) => !row.tools.includes(t) && !(originalLabels && row.corpus === 'us_label' && t === 'investigator'),
        );
        return (
          <div key={row.corpus} className="rounded border border-slate-200 p-2 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-800">{corpusLabel}</span>
              <button
                type="button"
                className="text-xs px-2 py-0.5 border rounded text-red-700"
                aria-label={`Remove ${corpusLabel} from ${stepLabel}`}
                onClick={() => onChange(rows.filter((_, i) => i !== rowIdx))}
              >
                Remove
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {row.tools.map((tool) => (
                <span
                  key={tool}
                  className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-2 py-0.5"
                >
                  {catalogLabel(catalog?.tools, tool)}
                  <button
                    type="button"
                    className="text-slate-500"
                    aria-label={`Remove ${catalogLabel(catalog?.tools, tool)} from ${corpusLabel} in ${stepLabel}`}
                    onClick={() =>
                      onChange(
                        rows.map((r, i) =>
                          i === rowIdx ? { ...r, tools: r.tools.filter((t) => t !== tool) } : r,
                        ),
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              {row.tools.length === 0 && (
                <span className="text-xs text-amber-800">
                  No tool chosen — this source will not be used.
                </span>
              )}
              {available.length > 0 && (
                <select
                  className="border border-slate-300 rounded px-1 py-0.5 text-xs"
                  aria-label={`Add a tool to ${corpusLabel} in ${stepLabel}`}
                  value=""
                  onChange={(e) => {
                    const tool = e.target.value;
                    if (!tool) return;
                    onChange(
                      rows.map((r, i) =>
                        i === rowIdx ? { ...r, tools: [...r.tools, tool] } : r,
                      ),
                    );
                  }}
                >
                  <option value="">Add tool…</option>
                  {available.map((tool) => (
                    <option key={tool} value={tool}>
                      {catalogLabel(catalog?.tools, tool)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      })}
      {unusedCorpora.length > 0 && (
        <select
          className="border border-slate-300 rounded px-2 py-1 text-xs"
          aria-label={`Add a source to ${stepLabel}`}
          value=""
          onChange={(e) => {
            const corpus = e.target.value;
            if (!corpus) return;
            onChange([...rows, { corpus, tools: [] }]);
          }}
        >
          <option value="">Add source…</option>
          {unusedCorpora.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Whether a (non-analysis) step is eligible for companion Cogni query inputs
 * (spec §4.1 V3, §4.8): either it already carries `cogni_queries` (e.g. a
 * plan whose sources need repair — the editor must still offer the field to
 * fix it), or some chosen source row pairs Keyword and Cogni on the same
 * corpus, the shape a paired discovery step needs.
 */
function hasCogniCompanionSource(step: DraftStep): boolean {
  if (Array.isArray(step.scope['cogni_queries'])) return true;
  return step.sourceRows.some(
    (row) => row.tools.includes('keyword_search') && row.tools.includes('cogni_search'),
  );
}

/**
 * One or two companion Cogni query inputs (spec §4.1 `scope.cogni_queries`,
 * §4.8). Always shows at least one box — blank when there is no saved query
 * yet — so a newly-eligible step can be typed into immediately; `fromDraft`
 * drops blank boxes on save, not this component. V2 caps the field at two
 * entries, so "Add second" disappears once both boxes are shown.
 */
function CogniQueryEditor({
  stepIndex,
  queries,
  onChange,
}: {
  stepIndex: number;
  queries: string[];
  onChange: (queries: string[]) => void;
}) {
  const displayed = queries.length > 0 ? queries : [''];
  return (
    <div className="space-y-1" data-testid="cogni-query-editor">
      <span className="block text-xs text-slate-600">Cogni queries</span>
      {displayed.map((q, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            type="text"
            aria-label={`Step ${stepIndex + 1} Cogni query ${i + 1}`}
            className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
            value={q}
            onChange={(e) => {
              const next = [...displayed];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          {i === 1 && (
            <button
              type="button"
              className="text-xs px-2 py-1 border rounded text-red-700"
              onClick={() => onChange(displayed.slice(0, 1))}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {displayed.length < 2 && (
        <button
          type="button"
          className="text-xs px-2 py-1 border rounded"
          onClick={() => onChange([...displayed, ''])}
        >
          Add second Cogni query
        </button>
      )}
    </div>
  );
}

interface PlanPanelProps {
  plan: PlanVersionResponse;
  isOwner: boolean;
  busy: boolean;
  conflict: string | null;
  onSaveEdit: (content: PlanContent) => void;
  onRevise: (instruction: string) => Promise<string | null>;
  onApprove: (version: number, contentHash: string) => void;
  onSelectVersion: (version: number) => void;
  catalog: CapabilityCatalog | null;
  catalogFailed: boolean;
  onLoadCatalog: () => Promise<void>;
  /** The workstream's confirmed render-time layout (spec §5.3) — `detail`'s
   *  own field, refreshed by the same poll/reload that keeps `catalog` and
   *  everything else current. Never carried in `plan.content`. */
  /** Fired when the staged plan reveal has finished (see PlanRail). */
  onPlanRevealed?: () => void;
  /** Animate the staged reveal only the FIRST time the plan appears; false on
   *  a re-view (e.g. after leaving the editor) so it does not replay. */
  animatePlanReveal?: boolean;
}

function AutomaticSynthesisStep({ researchStepCount }: { researchStepCount: number }) {
  const input = `Complete output from Step ${researchStepCount}`;

  return (
    <div
      className="rounded-md border border-indigo-200 bg-indigo-50 p-3"
      data-testid="automatic-synthesis-step"
    >
      <p className="text-sm font-medium text-slate-900">
        {researchStepCount + 1}. Synthesize final report
      </p>
      <p className="mt-2 text-sm text-slate-700">
        <span className="font-medium">Input:</span> {input}
      </p>
      <p className="mt-1 text-sm text-slate-700">
        <span className="font-medium">Action:</span>{' '}
        Analyze patterns and answer the original request
      </p>
      <p className="mt-1 text-sm text-slate-600">
        <span className="font-medium">Source:</span> Final pipeline step only
      </p>
      <p className="mt-1 text-xs font-medium text-indigo-700">
        Automatic · Read-only
      </p>
    </div>
  );
}

function PlanPanel({
  plan,
  isOwner,
  busy,
  conflict,
  onSaveEdit,
  onRevise,
  onApprove,
  onSelectVersion,
  catalog,
  catalogFailed,
  onLoadCatalog,
  onPlanRevealed,
  animatePlanReveal = true,
}: PlanPanelProps) {
  const [editing, setEditing] = useState(false);
  const [revising, setRevising] = useState(false);
  const [revisionInstruction, setRevisionInstruction] = useState('');
  const [revisionError, setRevisionError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftStep[]>(() => toDraft(plan.content?.steps));
  /**
   * The template the owner PICKED this edit, or `null` for "did not touch it".
   * The distinction is load-bearing: `report_template_request` is re-resolved
   * server-side against the registry's CURRENT version, so sending one on
   * every save would silently re-pin a plan whose carried pin is an older
   * version of the same id. Untouched ⇒ no request ⇒ the pin is carried
   * verbatim (`plan_service._materialize_report_template`).
   */
  const [templateRequest, setTemplateRequest] = useState<string | null>(null);
  const cohort = plan.content.contract === 'cohort_analysis_v1';

  useEffect(() => {
    setDraft(toDraft(plan.content?.steps));
    setEditing(false);
    setRevising(false);
    setRevisionInstruction('');
    setRevisionError(null);
    setTemplateRequest(null);
  }, [plan.version, plan.content]);

  const beginEdit = useCallback(async () => {
    setEditing(true);
    await onLoadCatalog();
  }, [onLoadCatalog]);

  // `ReportTemplatePin` renders on the REVIEW surface below, not only inside
  // the editor, and it reads the catalog to name the pinned template — which
  // for a contract plan is now its contract's own report template, not a
  // synthetic id. `beginEdit` above loads the catalog too late for that, so a
  // contract plan fetches it proactively; `loadCapabilityCatalog` is an
  // idempotent GET, so a later `beginEdit` call re-running it is harmless.
  useEffect(() => {
    if (plan.content.analysis_contract) void onLoadCatalog();
  }, [plan.content.analysis_contract, onLoadCatalog]);

  const updateStep = (idx: number, patch: Partial<DraftStep>) => {
    setDraft((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const moveStep = (idx: number, delta: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length || (cohort && (prev[idx]?.kind === 'analysis' || prev[target]?.kind === 'analysis'))) return prev;
      const item = next.splice(idx, 1)[0];
      if (!item) return prev;
      next.splice(target, 0, item);
      return reindexInputs(prev, next);
    });
  };
  // Both ordinal lists are remapped, for the same reason: a move or delete
  // renumbers the steps, and a stale one-based ordinal does not fail — it
  // silently names a DIFFERENT step. `report_bindings` (§7.4) is remapped
  // alongside `input_from_steps` but never merged into it; they stay
  // independent lists (a binding is not an acquisition edge).
  const reindexInputs = (prev: DraftStep[], next: DraftStep[]) => cohort ? next.map((step) => {
    const remap = (ordinals: number[]) => ordinals.flatMap((ordinal) => {
      const index = next.indexOf(prev[Number(ordinal) - 1]!);
      return index >= 0 ? [index + 1] : [];
    });
    const scope = { ...step.scope };
    let changed = false;
    for (const key of ['input_from_steps', 'report_bindings']) {
      const ordinals = scope[key];
      if (Array.isArray(ordinals)) {
        scope[key] = remap(ordinals as number[]);
        changed = true;
      }
    }
    return changed ? { ...step, scope } : step;
  }) : next;
  const deleteStep = (idx: number) => setDraft((prev) => prev[idx]?.kind === 'analysis' && cohort ? prev : reindexInputs(prev, prev.filter((_, i) => i !== idx)));
  const addStep = () =>
    setDraft((prev) => {
      const step: DraftStep = { goal: '', query: '', scope: {}, success_criterion: '', sourceRows: [], ...(cohort ? { kind: 'selection' as const } : {}) };
      return cohort ? [...prev.slice(0, -1), step, ...prev.slice(-1)] : [...prev, step];
    });

  return (
    <section className="space-y-4" data-testid="plan-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-900">
          Plan — version {plan.version}
        </h2>
        <div className="flex items-center gap-2">
          {plan.available_versions.length > 1 && (
            <label className="text-sm text-slate-600">
              Version{' '}
              <select
                aria-label="Plan version"
                className="border border-slate-300 rounded px-2 py-1 text-sm"
                value={plan.version}
                onChange={(e) => onSelectVersion(Number(e.target.value))}
              >
                {plan.available_versions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </label>
          )}
          {!plan.content.analysis_contract && (
            <span className="text-xs text-slate-500" data-testid="plan-content-hash">
              hash {plan.content_hash}
            </span>
          )}
        </div>
      </div>

      <CapabilityViolations plan={plan} />
      <UnprobedScopeNotice plan={plan} />

      {conflict && (
        <div
          className="rounded-md border border-red-300 bg-red-50 p-3"
          role="alert"
          data-testid="approval-conflict"
        >
          <p className="text-sm font-medium text-red-900">
            This plan changed while you were reviewing it
          </p>
          <p className="text-sm text-red-900 mt-1">{conflict}</p>
          <p className="text-sm text-red-900 mt-1">
            Nothing was approved. Review the current version and approve it again.
          </p>
        </div>
      )}

      {editing ? (
        <div className="space-y-3" data-testid="plan-editor">
          {draft.map((step, idx) => (
            <div key={idx} className="rounded-md border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">{cohort && step.kind === 'analysis' ? 'Acquire evidence' : `Step ${idx + 1}`}</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border rounded"
                    onClick={() => moveStep(idx, -1)}
                    disabled={cohort && step.kind === 'analysis'}
                    aria-label={`Move step ${idx + 1} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border rounded"
                    onClick={() => moveStep(idx, 1)}
                    disabled={cohort && (step.kind === 'analysis' || draft[idx + 1]?.kind === 'analysis')}
                    aria-label={`Move step ${idx + 1} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border rounded text-red-700"
                    onClick={() => deleteStep(idx)}
                    disabled={cohort && step.kind === 'analysis'}
                    aria-label={`Delete step ${idx + 1}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {!(cohort && step.kind === 'analysis') && <label className="block text-xs text-slate-600">
                Goal
                <textarea
                  aria-label={`Step ${idx + 1} goal`}
                  className="mt-1 w-full border border-slate-300 rounded p-2 text-sm"
                  value={step.goal}
                  onChange={(e) => updateStep(idx, { goal: e.target.value })}
                />
              </label>}
              <label className="block text-xs text-slate-600">
                {cohort && step.kind === 'analysis' ? 'Analysis question' : 'Query'}
                <textarea
                  aria-label={cohort && step.kind === 'analysis' ? 'Analysis question' : `Step ${idx + 1} query`}
                  className="mt-1 w-full border border-slate-300 rounded p-2 text-sm"
                  placeholder="The search terms or question this step runs"
                  value={step.query ?? ''}
                  onChange={(e) => updateStep(idx, { query: e.target.value })}
                />
              </label>
              {!(cohort && step.kind === 'analysis') && hasCogniCompanionSource(step) && (
                <CogniQueryEditor
                  stepIndex={idx}
                  queries={cogniQueries(step.scope)}
                  onChange={(queries) => {
                    const scope = { ...step.scope };
                    if (queries.length > 0) scope['cogni_queries'] = queries;
                    else delete scope['cogni_queries'];
                    updateStep(idx, { scope });
                  }}
                />
              )}
              {cohort && step.kind === 'analysis' && <fieldset className="text-sm space-y-1">
                <legend className="text-xs text-slate-600">Input cohorts (combined without duplicates)</legend>
                {draft.slice(0, idx).map((input, inputIdx) => <label key={inputIdx} className="block">
                  <input type="checkbox" aria-label={`Analysis input step ${inputIdx + 1}`} checked={Array.isArray(step.scope['input_from_steps']) && step.scope['input_from_steps'].includes(inputIdx + 1)} onChange={(event) => {
                    const inputs = Array.isArray(step.scope['input_from_steps']) ? step.scope['input_from_steps'] as number[] : [];
                    updateStep(idx, { scope: { ...step.scope, input_from_steps: event.target.checked ? [...inputs, inputIdx + 1].sort((a, b) => a - b) : inputs.filter((value) => value !== inputIdx + 1) } });
                  }} /> {inputIdx + 1}. {input.goal}
                </label>)}
                <AnalysisLimits budget={plan.content.analysis_budget} />
              </fieldset>}
              {cohort && step.kind === 'analysis' && <>
                <ReportTemplatePin
                  pin={plan.content.report_template}
                  templates={catalog?.report_templates}
                />
                <ReportPlanEditor
                  stepIndex={idx}
                  scope={step.scope}
                  earlierSteps={draft.slice(0, idx)}
                  // A contract plan's report template is its CONTRACT's
                  // (`ReportTemplatePin` above) — no options are offered, so no
                  // select renders. Passing the legacy catalog here anyway would
                  // offer a choice the server could never honour: the pin is
                  // derived from the contract, and any request naming another
                  // template is rejected as incompatible.
                  templates={plan.content.analysis_contract ? undefined : catalog?.report_templates}
                  selectedTemplateId={templateRequest ?? plan.content.report_template?.template_id ?? ''}
                  onSelectTemplate={setTemplateRequest}
                  onChangeBindings={(bindings) => updateStep(idx, { scope: { ...step.scope, report_bindings: bindings } })}
                />
              </>}
              {catalogFailed ? (
                <p className="text-xs text-amber-800" data-testid="catalog-unavailable">
                  Sources cannot be edited right now — the list of available
                  documents did not load. Your goal and query edits still save,
                  and this step keeps the sources it already has.
                </p>
              ) : catalog === null ? (
                /* NOT merely a spinner. `beginEdit` opens the editor
                   synchronously and only then awaits the catalog, while
                   `toDraft` has ALREADY populated `sourceRows` from the plan's
                   existing requirements. Rendering SourceEditor in that window
                   makes `catalogLabel` fall through to its raw-code fallback,
                   so a planner-chosen source flashes on screen as "sba" /
                   "read_file" — the one thing this surface must never show as
                   text. Withhold the rows until their labels exist. */
                <p className="text-xs text-slate-500" data-testid="catalog-loading">
                  Loading the list of available sources…
                </p>
              ) : (
                <SourceEditor
                  rows={step.sourceRows}
                  catalog={catalog}
                  stepLabel={`step ${idx + 1}`}
                  originalLabels={cohort && step.kind === 'analysis' && step.scope['version_anchor'] === 'origin'}
                  onChange={(rows) => updateStep(idx, { sourceRows: rows })}
                />
              )}
              {cohort && step.kind === 'analysis' && step.scope['version_anchor'] === 'origin' && step.sourceRows.some((row) => row.corpus === 'us_label') && <p className="text-xs text-amber-800">Investigator does not support Original US labels. Use an approved search or read tool for that source.</p>}
              {step.sourceRows.some((row) => row.corpus === 'us_label') && (
                <label className="block text-xs text-slate-600">
                  Label version
                  <select
                    aria-label={`Step ${idx + 1} label version`}
                    className="mt-1 block border border-slate-300 rounded px-2 py-1 text-sm"
                    value={step.scope?.['version_anchor'] === 'origin' ? 'origin' : 'current'}
                    onChange={(event) => updateStep(idx, {
                      scope: { ...(step.scope ?? {}), version_anchor: event.target.value },
                    })}
                  >
                    <option value="current">Current</option>
                    <option value="origin">Original</option>
                  </select>
                </label>
              )}
            </div>
          ))}
          {!cohort && draft.length > 0 && (
            <AutomaticSynthesisStep researchStepCount={draft.length} />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm border rounded"
              onClick={addStep}
            >
              Add step
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                // A previously STORED `report_template_request` (e.g. the
                // server's own `incompatible_with_analysis_contract` marker)
                // must never ride along on a save it wasn't part of — spread
                // verbatim, it would be re-sent, re-marked, and keep the plan
                // unapprovable forever even after the chooser is gone (or
                // never touched again).
                const { report_template_request: _storedTemplateRequest, ...contentWithoutStoredRequest } = plan.content;
                void _storedTemplateRequest;
                onSaveEdit({
                  ...contentWithoutStoredRequest,
                  steps: draft.map(fromDraft),
                  // Only an ACTUAL pick is sent. `report_template` is spread
                  // through from the loaded plan and is stripped server-side
                  // for every contract before resolution, so it cannot smuggle
                  // a client-chosen version past the pin.
                  ...(templateRequest === null ? {} : { report_template_request: { template_id: templateRequest } }),
                });
              }}
            >
              Save as new version
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm border rounded"
              onClick={() => {
                setDraft(toDraft(plan.content?.steps));
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Spec §4: the plan "comes together" BEFORE approval — a vertical bar,
        // then circles, then each step's exact text written out — via PlanRail
        // (the review-stage counterpart to the post-approval execution
        // StepRail). Each item renders as one <li> under
        // `data-testid="plan-steps"` — including the automatic synthesis step —
        // so the list-item count and every inner test-id are preserved.
        <PlanRail
          listTestId="plan-steps"
          onRevealed={onPlanRevealed}
          animate={animatePlanReveal}
          items={[
            ...(plan.content?.steps ?? []).map((step, idx) => (
              <div key={idx} className="rounded-md border border-slate-200 bg-white p-3">
                {/* Step title is intentionally larger than the Query:/Sources:
                    sub-lines below it (spec). */}
                <p className="text-base font-semibold text-slate-900">
                  {idx + 1}. <span>{cohort && step.kind === 'analysis' ? 'Acquire evidence' : step.goal}</span>
                </p>
                {/* The planner has always emitted `query` and its own schema
                    says a reviewer edits it before approving — but it is a
                    TOP-LEVEL step field, not a `scope` key, so `scopeEntries`
                    never reached it and it was invisible on this surface until
                    2026-08-31. It is the most concrete thing on a plan: the
                    literal terms that will run. */}
                {step.query && (
                  <p className="mt-2 text-sm text-slate-700" data-testid="step-query">
                    <span className="font-medium">Query:</span> {step.query}
                  </p>
                )}
                {/* Each companion Cogni query gets its own line (spec §4.8) —
                    a comma-joined `scopeEntries` value would be ambiguous with
                    commas inside a natural-language query. */}
                {cogniQueries(step.scope).map((q, i) => (
                  <p key={i} className="mt-2 text-sm text-slate-700" data-testid="step-cogni-query">
                    <span className="font-medium">Cogni query {i + 1}:</span> {q}
                  </p>
                ))}
                {scopeEntries(step.scope, catalog?.ha_document_types).length > 0 && (
                  <dl className="mt-2 text-sm text-slate-600 space-y-0.5">
                    {scopeEntries(step.scope, catalog?.ha_document_types).map(([k, v]) => (
                      <div key={k} className="flex gap-1">
                        <dt className="font-medium capitalize">{k === 'label version' ? 'Label version' : k}:</dt>
                        <dd>{v}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {/* "Done when" (success_criterion) is no longer shown: nothing
                    enforces it — `verifier.py` never reads it — so it restated
                    the goal without adding information (user decision
                    2026-08-30). The field still reaches the executor prompt.
                    What a reviewer actually needs before approving is WHICH
                    DOCUMENTS the step will read. */}
                {step.sources && step.sources.length > 0 && (
                  <p className="mt-2 text-sm text-slate-600" data-testid="step-sources">
                    <span className="font-medium">Sources:</span>{' '}
                    {step.sources.join(' · ')}
                  </p>
                )}
                {step.continue_on_failure && (
                  <p className="mt-1 text-xs text-slate-500">
                    Later steps continue even if this one fails.
                  </p>
                )}
                {cohort && step.kind === 'analysis' && <>
                  <ReportTemplatePin
                    pin={plan.content.report_template}
                    templates={catalog?.report_templates}
                  />
                  <AnalysisLimits budget={plan.content.analysis_budget} />
                </>}
              </div>
            )),
            ...(!cohort && (plan.content?.steps?.length ?? 0) > 0
              ? [
                <AutomaticSynthesisStep
                  key="synthesis"
                  researchStepCount={plan.content?.steps?.length ?? 0}
                />,
              ]
              : []),
          ]}
        />
      )}

      {!editing && plan.content.analysis_contract && (
        <AnalysisSetup contract={plan.content.analysis_contract} />
      )}

      {isOwner && !editing && (
        <div className="space-y-3">
          {revising && plan.content.analysis_contract && (
            <div className="rounded-md border border-indigo-200 bg-white p-3 space-y-2">
              <label className="block text-sm font-medium text-slate-800">
                Requested analysis change
                <textarea
                  aria-label="Requested analysis change"
                  className="mt-1 w-full rounded border border-slate-300 p-2 text-sm"
                  maxLength={4000}
                  rows={3}
                  placeholder="Describe what should change in the analysis"
                  value={revisionInstruction}
                  onChange={(event) => setRevisionInstruction(event.target.value)}
                />
              </label>
              <p className="text-xs text-slate-600">
                The planner will update the analysis instructions and questions together in a new plan version.
              </p>
              {revisionError && (
                <p role="alert" className="text-sm text-red-800">{revisionError}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded disabled:opacity-50"
                  disabled={busy || revisionInstruction.trim().length === 0}
                  onClick={() => void onRevise(revisionInstruction.trim()).then(setRevisionError)}
                >
                  Create revised plan
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded"
                  disabled={busy}
                  onClick={() => { setRevising(false); setRevisionInstruction(''); setRevisionError(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-sm border rounded"
            onClick={() => void beginEdit()}
          >
            Edit plan
          </button>
          {plan.content.analysis_contract && (
            <button
              type="button"
              className="px-3 py-1.5 text-sm border rounded"
              onClick={() => setRevising((value) => !value)}
              disabled={busy}
            >
              Revise analysis setup
            </button>
          )}
          <button
            type="button"
            className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded disabled:opacity-50"
            disabled={busy || !plan.is_approvable || plan.is_approved}
            // Approval carries the version AND the hash of the content on
            // screen (spec §3.3 approval integrity). Approving by workstream id
            // alone would approve whatever landed since this render.
            onClick={() => onApprove(plan.version, plan.content_hash)}
          >
            {plan.is_approved ? 'Approved' : `Approve version ${plan.version}`}
          </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Live progress
// ---------------------------------------------------------------------------

type ToolAuditState =
  | { kind: 'loading' }
  | { kind: 'error'; status: number }
  | { kind: 'loaded'; data: StepToolCallsResponse };

function resultRows(result: Record<string, unknown> | null): unknown[] {
  const payload = result?.['payload'];
  if (!payload || typeof payload !== 'object') return [];
  return Object.values(payload as Record<string, unknown>).flatMap((value) =>
    Array.isArray(value) ? value : [],
  );
}

function rowText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (value && typeof value === 'object') {
    const row = value as Record<string, unknown>;
    const version = [
      row['version_number'] != null ? `Version ${row['version_number']}` : null,
      typeof row['version_date'] === 'string' ? row['version_date'] : null,
      typeof row['version_note'] === 'string' ? row['version_note'] : null,
    ].filter(Boolean).join(' · ');
    for (const key of ['title', 'name', 'label', 'text']) {
      if (typeof row[key] === 'string') {
        return version ? `${row[key]} ${version}` : row[key] as string;
      }
    }
    if (typeof row['headline'] === 'string') {
      const locator = [row['drug_id'] != null ? `Drug ${row['drug_id']}` : null, row['section_number'] != null ? `Section ${row['section_number']}` : null].filter(Boolean).join(' · ');
      const headline = row['headline'].replace(/<\/?mark>/g, '');
      return locator ? `${locator}: ${headline}` : headline;
    }
  }
  return JSON.stringify(value);
}

function ToolCallRows({ calls }: { calls: ToolCallAudit[] }) {
  return <div className="space-y-3">
    {calls.map((call) => {
      const rows = resultRows(call.result);
      const query = call.args['query'] ?? call.args['keyword'];
      const partial = Boolean(call.result?.['payload'] && typeof call.result['payload'] === 'object' &&
        (call.result['payload'] as Record<string, unknown>)['partial'] === true);
      return <article key={`${call.attempt}-${call.call_ordinal}`} className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 space-y-1 break-words">
        <p className="text-sm font-medium">{call.tool_label}{call.corpus_label ? ` · ${call.corpus_label}` : ''}</p>
        {call.document_type_labels.length > 0 && <p className="text-xs text-slate-600">Document types: {call.document_type_labels.join(', ')}</p>}
        {typeof query === 'string' && <p className="text-sm">Search: {query}</p>}
        {Array.isArray(call.args['regions']) && <p className="text-xs">Regions: {call.args['regions'].join(', ')}</p>}
        {Array.isArray(call.args['sections']) && <p className="text-xs">Sections: {call.args['sections'].join(', ')}</p>}
        {(call.args['version_anchor'] === 'current' || call.args['version_anchor'] === 'origin') && (
          <p className="text-xs">Label version: {call.args['version_anchor'] === 'origin' ? 'Original' : 'Current'}</p>
        )}
        {partial && <p className="text-sm text-amber-800" data-testid={`tool-audit-partial-${call.attempt}-${call.call_ordinal}`}>This result is partial.</p>}
        {call.legacy_payload_only && <p className="text-sm text-amber-800" data-testid={`tool-audit-legacy-${call.attempt}-${call.call_ordinal}`}>Legacy result: only the original payload is available.</p>}
        {rows.length > 0 && <ul className="mt-1 list-disc pl-5">{rows.map((row, index) => <li key={index} className="text-sm">{rowText(row)}</li>)}</ul>}
        <details data-testid={`tool-audit-raw-${call.attempt}-${call.call_ordinal}`} className="mt-2">
          <summary className="rounded text-xs cursor-pointer text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">Raw JSON</summary>
          <pre data-testid={`tool-audit-raw-json-${call.attempt}-${call.call_ordinal}`} className="mt-2 max-h-96 overflow-auto rounded bg-slate-50 p-3 text-xs whitespace-pre-wrap break-all" tabIndex={0} aria-label="Raw tool result JSON">{JSON.stringify(call, null, 2)}</pre>
        </details>
      </article>;
    })}
  </div>;
}

function CollapseResultsButton({ children }: { children: React.ReactNode }) {
  return <button type="button" className="mt-2 rounded px-2 py-1 text-sm text-indigo-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600" onClick={(event) => {
    const details = event.currentTarget.closest('details');
    if (details) {
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  }}>{children}</button>;
}

function CandidateReferences({ references }: { references: CandidateReference[] }) {
  return <ul className="mt-3 space-y-3 border-l-2 border-slate-100 pl-3 sm:ml-2">{references.map((ref, index) => {
    const parsed = parseEvidencePath(ref.deep_link);
    const target = parsed ? resolveEvidenceTarget(parsed) : ref.deep_link;
    const drugFallback = target?.startsWith('/drug/') && !target.includes('?');
    return <li key={index} className="min-w-0 space-y-1 text-xs text-slate-700 break-words">
      <p className="font-medium text-slate-900">{ref.corpus_label ?? 'Source'}{ref.document_id != null ? ` · Document ${ref.document_id}` : ''}</p>
      <p className="break-all">Version/anchor {ref.version_anchor} ({ref.anchor_kind})</p>
      <p>{[ref.section_number != null ? `Section ${ref.section_number}` : null, ref.letter_part != null ? `Part ${ref.letter_part}` : null, ref.page != null ? `Page ${ref.page}` : null, ref.chunk_id != null ? `Chunk ${ref.chunk_id}` : null].filter(Boolean).join(' · ')}</p>
      <p className="text-slate-500">Retrieved {ref.retrieved_at}</p>
      {ref.matching_text && <div className="rounded bg-slate-50 p-2"><p className="font-medium text-slate-900">Matching text</p><p className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words" tabIndex={0} aria-label="Matching text">{ref.matching_text}</p></div>}
      {target ? <a className="inline-block rounded py-1 text-indigo-700 underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600" href={ref.deep_link} onClick={(e) => openInAppLink(e, ref.deep_link)} target="_blank" rel="noreferrer" aria-label={`${drugFallback ? 'Open drug page' : 'Open source'} (opens in a new tab)`}>{drugFallback ? 'Open drug page' : 'Open source'}</a> : <p>No viewer for this source yet.</p>}
      {drugFallback && <p>The referenced document is not selected automatically.</p>}
    </li>;
  })}</ul>;
}

function StepCandidates({ state, onRetry }: { state: ToolAuditState | undefined; onRetry: () => void }) {
  if (!state || state.kind === 'loading') return <p role="status" className="mt-2 text-sm text-slate-600">Loading drug candidates…</p>;
  if (state.kind === 'error') return <div className="mt-2 text-sm text-amber-800"><p>{state.status === 401 || state.status === 403 ? 'You are not authorized to view these drug candidates.' : 'Drug candidates are unavailable.'}</p><button type="button" className="underline" onClick={onRetry}>Retry candidates</button></div>;
  const output = state.data.candidate_output;
  if (!output) return <p className="mt-2 text-sm text-slate-600">Drug candidates are unavailable for this published output.</p>;
  if (output.read_outcomes) {
    const reads = output.read_outcomes;
    return <div className="mt-3 space-y-2 text-sm">
      <p>{reads.documents_indexed} documents indexed; {reads.sections_read} sections read; {reads.empty_reads} empty reads.</p>
      {(output.partial || reads.empty_reads > 0 || reads.unresolved_targets > 0) && <p className="text-amber-800">Incomplete coverage{reads.unresolved_targets > 0 ? `; ${reads.unresolved_targets} unresolved targets` : ''}.</p>}
    </div>;
  }
  return <div className="mt-3 space-y-2">
    {output.capped && <p className="text-sm text-amber-800">A source limit was reached.</p>}
    {output.unresolved_matches && <p className="text-sm text-amber-800">Unresolved matches are excluded from this candidate count and downstream inputs. Inspect the tool results for those raw matches.</p>}
    <details className="group/drugs">
      <summary className="cursor-pointer rounded text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"><span className="font-medium">Found {output.candidate_count} drug candidates matching criteria.</span><span aria-hidden="true" className="ml-2 text-xs text-indigo-700"><span className="group-open/drugs:hidden">Show drugs</span><span className="hidden group-open/drugs:inline">Hide drugs</span></span></summary>
      <div className="mt-2 space-y-2">
    {output.candidates.map((candidate) => <details key={candidate.drug_id} className="group/candidate rounded border border-slate-200 px-3 py-2">
      <summary className="cursor-pointer rounded text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"><span className="font-medium">{candidate.drug_name}</span><span className="ml-2 text-xs text-slate-600">{candidate.references.length} {candidate.references.length === 1 ? 'reference' : 'references'}</span><span aria-hidden="true" className="ml-2 text-xs text-indigo-700"><span className="group-open/candidate:hidden">Show references</span><span className="hidden group-open/candidate:inline">Hide references</span></span></summary>
      <CandidateReferences references={candidate.references} />
      <CollapseResultsButton>Hide references</CollapseResultsButton>
    </details>)}
    {output.unassociated_references.length > 0 && <details className="rounded border border-slate-200 px-3 py-2"><summary className="cursor-pointer rounded text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600">References without a drug association<span className="ml-2 text-xs text-slate-600">{output.unassociated_references.length} {output.unassociated_references.length === 1 ? 'reference' : 'references'}</span></summary><CandidateReferences references={output.unassociated_references} /><CollapseResultsButton>Hide references</CollapseResultsButton></details>}
      </div>
      <CollapseResultsButton>Hide drugs</CollapseResultsButton>
    </details>
  </div>;
}

function ToolAuditDetails({ step, state, candidateState, onOpen }: { step: StepSummary; state: ToolAuditState | undefined; candidateState?: ToolAuditState | undefined; onOpen: (candidatesOnly?: boolean) => void }) {
  const skipped = step.status === 'skipped';
  return <>{!skipped && <StepCandidates state={candidateState} onRetry={() => onOpen(true)} />}<details data-testid={`tool-audit-${step.ordinal}`} className="group/audit mt-3" onToggle={(event) => {
    if ((event.currentTarget as HTMLDetailsElement).open && !skipped && (!state || state.kind === 'error')) onOpen();
  }}>
    <summary className="rounded text-sm cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"><span>Tool inputs and results</span><span aria-hidden="true" className="ml-2 text-xs text-indigo-700"><span className="group-open/audit:hidden">Show</span><span className="hidden group-open/audit:inline">Hide</span></span></summary>
    <div className="mt-2">
      {skipped ? <p data-testid={`tool-audit-skipped-${step.ordinal}`} className="text-sm text-slate-600">No tool output: this step was skipped.</p> : null}
      {state?.kind === 'loading' && <p role="status" data-testid={`tool-audit-loading-${step.ordinal}`} className="text-sm">Loading tool inputs and results…</p>}
      {state?.kind === 'error' && (state.status === 401 || state.status === 403 ? <p data-testid={`tool-audit-auth-${step.ordinal}`} className="text-sm text-amber-800">You are not authorized to view these tool details.</p> : <div className="flex items-center gap-2"><p data-testid={`tool-audit-error-${step.ordinal}`} className="text-sm text-red-800">Could not load tool inputs and results.</p><button type="button" className="text-sm text-indigo-700 underline" onClick={() => onOpen()}>Retry</button></div>)}
      {state?.kind === 'loaded' && state.data.calls.length === 0 && <p data-testid={`tool-audit-empty-${step.ordinal}`} className="text-sm text-slate-600">{state.data.execution?.['empty_input'] === true ? 'No applicable inputs were published for this step.' : 'No tool calls were published for this step.'}</p>}
      {state?.kind === 'loaded' && state.data.calls.length > 0 && <section data-testid={`tool-audit-authoritative-${step.ordinal}`}><h4 className="text-sm font-medium">Published output</h4><ToolCallRows calls={state.data.calls} /></section>}
      {state?.kind === 'loaded' && state.data.history_calls.length > 0 && <details data-testid={`tool-audit-history-${step.ordinal}`} className="mt-2"><summary className="text-sm cursor-pointer">Retry and abandoned history</summary><ToolCallRows calls={state.data.history_calls} /></details>}
      <CollapseResultsButton>Hide tool results</CollapseResultsButton>
    </div>
  </details></>;
}

// Spec §15: the terminal acquisition step keeps its stored `kind="analysis"`
// and its acquisition identity in the title, and the two stages that follow it
// — "Analyze evidence" and "Write report" — render as automatic PROGRESS
// STAGES over that one step's `analysis_phase`. There is no third `AgentStep`
// row here and none is implied (AC8): these are presentation stages, so both
// rows read the SAME phase field and can never disagree about which is current.
const ANALYSIS_PHASE_ORDER: AnalysisPhase[] = ['acquisition', 'analysis', 'report', 'complete'];
const ANALYSIS_STAGES: { phase: AnalysisPhase; label: string }[] = [
  { phase: 'analysis', label: 'Analyze evidence' },
  { phase: 'report', label: 'Write report' },
];
const STAGE_STATE_WORD: Record<string, string> = {
  done: 'complete', active: 'in progress', pending: 'not started', idle: 'not running',
};

function AnalysisStageRows({ ordinal, phase, running }: { ordinal: number; phase: AnalysisPhase; running: boolean }) {
  const reached = ANALYSIS_PHASE_ORDER.indexOf(phase);
  return (
    <ol className="mt-2 space-y-1" data-testid={`analysis-stages-${ordinal}`}>
      {ANALYSIS_STAGES.map(({ phase: stagePhase, label }) => {
        const at = ANALYSIS_PHASE_ORDER.indexOf(stagePhase);
        // `running` is the step's PROMOTED status (`displayStatusOf`), not a
        // raw status or a bare phase comparison: that is what keeps a
        // cancelled/failed workstream with a stale `analysis_phase` from
        // showing an active stage here (§15). One guard, three readers.
        const state = reached > at ? 'done' : reached < at ? 'pending' : running ? 'active' : 'idle';
        return (
          <li
            key={stagePhase}
            data-testid={`analysis-stage-${stagePhase}-${ordinal}`}
            data-state={state}
            aria-label={`${label} — ${STAGE_STATE_WORD[state]}`}
            className={`flex items-center gap-2 text-sm ${state === 'done' || state === 'active' ? 'text-slate-700' : 'text-slate-500'}`}
          >
            {state === 'active' ? (
              <span
                data-testid={`analysis-stage-spinner-${stagePhase}-${ordinal}`}
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full bg-blue-500 motion-safe:animate-pulse"
              />
            ) : (
              <span
                aria-hidden="true"
                className={`h-2 w-2 shrink-0 rounded-full ${state === 'done' ? 'bg-emerald-400' : 'border border-slate-300'}`}
              />
            )}
            <span>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function StepProgressRow({ step, auditState, candidateState, onOpenAudit, haDocumentTypes, report, workstreamStatus }: { step: StepSummary; auditState?: ToolAuditState; candidateState?: ToolAuditState | undefined; onOpenAudit: (candidatesOnly?: boolean) => void; haDocumentTypes?: { value: string; label: string }[]; report?: WorkstreamReport | null; workstreamStatus?: string | null }) {
  const skipped = step.status === 'skipped';
  const analysis = step.kind === 'analysis';
  const phase = analysis ? step.analysis_phase : null;
  const coverage = report?.analysis_coverage ?? step.analysis_coverage;
  // The rail (StepRail.tsx), the header's running-step line and the two stage
  // rows below all key off this same promotion — keeping every reader of a
  // step's displayed status on one function is what keeps them from
  // disagreeing on one row.
  const displayStatus = displayStatusOf(step, workstreamStatus);
  return (
    <div
      className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={`progress-step-${step.ordinal}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        {/* Step title larger than the sub-detail lines below it (spec). */}
        <h3 className="min-w-0 text-base font-semibold text-slate-900 break-words">
          {step.ordinal}. {analysis ? 'Acquire evidence' : step.goal}
        </h3>
        <span className={`${stepStatusBadge(displayStatus)} shrink-0`}>
          {stepStatusLabel(displayStatus)}
        </span>
      </div>
      {step.cached && step.cache_source_completed_at && (
        <p className="mt-1 text-xs text-indigo-800" title="Reused an acquisition snapshot that was at most 24 hours old when this step ran; new documents may not be included. The report is generated afresh.">
          Cached · {new Date(step.cache_source_completed_at).toLocaleString()}
        </p>
      )}
      {phase && <AnalysisStageRows ordinal={step.ordinal} phase={phase} running={displayStatus === 'running'} />}
      {analysis && coverage && <p className="mt-1 text-sm text-slate-600" data-testid="analysis-coverage">{coverage.selected_candidates} selected · {coverage.analyzed_candidates} evidence reviewed · {coverage.unresolved_candidates} with evidence gaps</p>}

      {/* Carried-forward provenance (plan T6.6): this step is not new work, it
          was preserved across a re-plan. Rendering it as an ordinary step
          loses which approved version it actually came from. */}
      {step.carried_from_plan_version !== null &&
        step.carried_from_plan_version !== undefined && (
          <p className="mt-1 text-xs text-indigo-800" data-testid="carried-forward">
            Carried forward from plan version {step.carried_from_plan_version}
          </p>
        )}

      {/* §8.4: a skipped step is NEVER an empty section — it carries its reason. */}
      {skipped && (
        <p className="mt-1 text-sm text-amber-800" data-testid="skip-reason">
          Skipped — {step.skip_reason ?? 'no reason was recorded for this skip.'}
        </p>
      )}

      {step.status === 'failed' && step.error_user_message && (
        <p className="mt-1 text-sm text-red-800" data-testid="step-error">
          Failed — {step.error_user_message}
        </p>
      )}

      {step.flag_reasons.length > 0 && (
        <ul className="mt-1 space-y-0.5" data-testid="step-flags">
          {step.flag_reasons.map((r, i) => (
            <li key={i} className="text-sm text-amber-900">Flagged: {r}</li>
          ))}
        </ul>
      )}
      {scopeEntries(step.scope, haDocumentTypes).filter(([key]) => key === 'document types' || key === 'label version').map(([key, value]) => (
        <p key={key} className="mt-1 text-xs text-slate-600">{key === 'document types' ? 'Document types' : 'Label version'}: {value}</p>
      ))}
      {(step.status === 'done' || step.status === 'flagged' || step.status === 'failed' || skipped || step.carried_from_plan_version != null) && <ToolAuditDetails step={step} state={auditState} candidateState={candidateState} onOpen={onOpenAudit} />}
      {analysis && report && <ReportView report={report} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staged report (spec §3.5, §8.4)
// ---------------------------------------------------------------------------

function ReportText({ text, testId }: { text: string; testId?: string }) {
  return <div
    // Rule 27: body prose is capped at a 72ch measure — a max-width screen of
    // text does not run the full width of a wide viewport.
    className="prose prose-sm prose-slate max-w-[72ch] min-w-0 overflow-x-auto break-words prose-headings:mt-5 prose-headings:mb-2 prose-p:my-3 prose-table:text-sm"
    data-testid={testId}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(text, { async: false }), { USE_PROFILES: { html: true } }) }}
  />;
}

// ---------------------------------------------------------------------------
// Templated report body (spec §15)
//
// THE BRANCH DISCRIMINATOR IS THE ABSENCE OF `claims` (§8.3, AC10). Not a
// feature flag, not a global switch, not the presence of a `report_template`
// pin — a legacy in-flight plan can carry that pin and still have written a
// claims body, and rendering IT as a template would silently drop its findings.
// The body's own shape decides, so a report re-read years after it was written
// still renders the way it was written.
// ---------------------------------------------------------------------------

function templatedBody(synthesis: SynthesisContent | null): TemplatedReportBody | null {
  if (!synthesis || 'claims' in synthesis) return null;
  // A body with no claims is only templated if it actually carries a template
  // body; otherwise (an older serialization) the legacy summary path still
  // renders it, rather than an empty section.
  const body = synthesis as unknown as TemplatedReportBody;
  return Array.isArray(body.slots) && Array.isArray(body.section_tree) ? body : null;
}

/**
 * §11.5's SECOND reference kind, rendered as itself.
 *
 * A computed-slot reference names a slot of the ASSEMBLED REPORT and resolves
 * against the pinned projection. It addresses no document: it has no corpus, no
 * section, no quoted span, no deep link and no retrieval timestamp, because
 * nothing was retrieved — the number is arithmetic over the pinned snapshot.
 *
 * **Never route one through `CitationRow`.** Manufacturing an `EvidenceRef` for
 * it would put a citation on screen that points at no document, and a reader
 * cannot tell a fabricated source from a real one. That is the single rule this
 * component exists to enforce.
 */
// Rule 16 (circular-percentage clause): these slots' 100% is circular by
// construction (the cohort IS "has a PRO record"/"has a recorded value"), not
// a genuine finding. No projection field marks this, so the ids — stable per
// their template — are named directly; a bounded renderer change, not a
// heuristic that could misfire on an unrelated, legitimately-100% count.
// `c1_1_products_with_pro`/`c4_1_with_pros` are v1 ids, kept (not replaced)
// because completed v1 reports render from stored JSON; `c3_1_with_pros` is
// the restructured PRO template's (2026-09-19) renamed equivalent of
// `c4_1_with_pros`.
const CIRCULAR_PERCENTAGE_SLOT_IDS = new Set(['c1_1_products_with_pro', 'c4_1_with_pros', 'c3_1_with_pros']);

/** A `_project_count` percentage cell always looks like `"12.5% (3/24
 *  subjects)"` (`projection.py:_percentage`). Detecting the SHAPE, not just
 *  the column header, is what lets the table-cell path apply rule 16
 *  wherever this string actually lands — including a `%` column whose header
 *  the backend could rename without this check going stale. */
function isPercentageDisplayString(cell: unknown): cell is string {
  return typeof cell === 'string' && /^\d+(\.\d+)?% \(/.test(cell);
}

const CIRCULAR_PERCENTAGE_CELL_NOTE =
  'percentage omitted — circular by construction';

function computedValueText(value: ComputedSlotValue | undefined): string {
  if (!value) return 'This computed value is not present in the pinned projection.';
  switch (value.op) {
    case 'count': {
      // `percentage.display` already carries its denominator (§10). It is null
      // when the denominator is zero — a percentage of nothing is not 0%.
      if (value.percentage) {
        if (CIRCULAR_PERCENTAGE_SLOT_IDS.has(value.slot_id)) {
          return `${value.value} of ${value.percentage.denominator} ${value.percentage.unit}s — ` +
            'percentage omitted: this cohort is defined by having a recorded value here, so it is 100% by construction.';
        }
        return value.percentage.display;
      }
      const denominator = value.denominator;
      return denominator
        ? `${value.value} of ${denominator.value} ${denominator.unit}s`
        : `${value.value}`;
    }
    case 'percentage_of':
      return value.percentage
        ? value.percentage.display
        : 'No percentage: the declared denominator is zero.';
    default: {
      const denominator = value.denominator;
      return denominator
        ? `Distribution over ${denominator.value} ${denominator.unit}s`
        : 'Distribution over the pinned analysis snapshot.';
    }
  }
}

// ---------------------------------------------------------------------------
// Structured-cell representations (spec rules 7, 11, 12, 21b).
//
// `projection.py` emits exactly two shapes a cell or a reference can resolve
// to that are not already flat scalars: a `count_by_enum` slot (every enum
// row plus an `unsupported` count) and a `group_by` cell/`_distribution`
// (values + supported + unsupported + denominator). ONE component per shape,
// used by BOTH the table-cell path (`ProjectionTableView`) and the reference
// path (`ComputedSlotBody`/`ComputedBlockView`), so the two paths cannot
// render the same data two different ways.
// ---------------------------------------------------------------------------

interface GroupByDistribution {
  values: { value: string; count: number }[];
  supported: number;
  unsupported: number;
  denominator: { unit: string; value: number; basis: string };
}

function isComputedSlotValue(value: unknown): value is ComputedSlotValue {
  return !!value && typeof value === 'object' && !Array.isArray(value) &&
    typeof (value as Record<string, unknown>)['op'] === 'string';
}

function isProjectionTable(value: unknown): value is ProjectionTable {
  return !!value && typeof value === 'object' &&
    typeof (value as Record<string, unknown>)['table_id'] === 'string' &&
    Array.isArray((value as Record<string, unknown>)['rows']);
}

function isProjectionAppendix(value: unknown): value is ProjectionAppendix {
  return !!value && typeof value === 'object' &&
    typeof (value as Record<string, unknown>)['appendix_id'] === 'string' &&
    Array.isArray((value as Record<string, unknown>)['rows']);
}

/** `projection.references` entries (§0's `REFERENCE_FIELDS`): a curated
 *  guidance document, not a computed value — distinguished from every other
 *  computed-reference shape by carrying a `ref_id` and a `url`, neither of
 *  which a `ComputedSlotValue`/table/appendix has. */
function isReferenceEntry(value: unknown): value is ReferenceEntry {
  return !!value && typeof value === 'object' && !Array.isArray(value) &&
    typeof (value as Record<string, unknown>)['ref_id'] === 'string' &&
    typeof (value as Record<string, unknown>)['url'] === 'string';
}

function isGroupByDistribution(value: unknown): value is GroupByDistribution {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return Array.isArray(v['values']) && typeof v['supported'] === 'number' &&
    typeof v['unsupported'] === 'number' && !!v['denominator'] && typeof v['denominator'] === 'object';
}

/** Rule 7's `count_by_enum` representation: every row (zero-count rows KEPT —
 *  a zero is a finding), then the slot's `unsupported` count. Rule 11 (+ R-F/
 *  R-G): the frozen snapshot predates T1's fix and stores an `unsupported`
 *  that EXCEEDS its own denominator (`c7_2` 66 vs 25; `c6_2` 69 vs 7) — never
 *  clamped, dropped or relabeled as a subgroup count. It is labelled against
 *  the declared denominator only when that count is actually consistent with
 *  it; otherwise a neutral, population-wide label that claims no subgroup. */
function CountByEnumRepresentation({ value }: { value: ComputedSlotValue }) {
  const rows = (value['rows'] ?? []) as { value: string; label: string; count: number; percentage: ProjectionPercentage | null }[];
  const denominator = value.denominator ?? null;
  const unsupported = value['unsupported'] as { unit: string; value: number } | undefined;
  return (
    <div data-testid="count-by-enum">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="count-by-enum-empty">No enum values are declared for this slot.</p>
      ) : (
        <ul className="list-disc pl-5 text-sm text-slate-800">
          {rows.map((row) => (
            <li key={row.value}>
              {row.label} {row.count}
              {row.percentage ? ` (${row.percentage.display})`
                : denominator ? ` (of ${denominator.value} ${denominator.unit}s)` : ''}
            </li>
          ))}
        </ul>
      )}
      {unsupported && (
        <p className="text-sm text-slate-700 mt-1" data-testid="count-by-enum-unsupported">
          {denominator && unsupported.value <= denominator.value
            ? `Unsupported: ${unsupported.value} of ${denominator.value} ${denominator.unit}s`
            : `Unsupported: ${unsupported.value} ${unsupported.unit}s (a population-wide count — not scoped to the subgroup denominator above)`}
        </p>
      )}
    </div>
  );
}

/** Rule 7's `group_by` cell representation, ONE cohort column's
 *  `_distribution`. Rule 21b: `supported`/`unsupported` are OVERLAPPING
 *  distinct-subject counts over the same record set, never complementary
 *  parts of the denominator — stated as such, and the inequality is never
 *  "fixed" by shrinking `unsupported`. */
function GroupByCellRepresentation({ cell }: { cell: GroupByDistribution }) {
  const unit = cell.denominator.unit;
  return (
    <div data-testid="group-by-cell">
      {cell.values.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="group-by-cell-empty">No values are recorded for this cohort.</p>
      ) : (
        <ul className="list-disc pl-5 text-sm text-slate-800">
          {cell.values.map((entry, index) => (
            <li key={`${entry.value}-${index}`}>{entry.value} {entry.count}</li>
          ))}
        </ul>
      )}
      <p className="text-sm text-slate-700 mt-1" data-testid="group-by-cell-summary">
        supported {cell.supported}, unsupported {cell.unsupported}, of {cell.denominator.value} {unit}s;
        a {unit} with several records may appear in both, so these are not complementary parts of {cell.denominator.value}.
      </p>
    </div>
  );
}

/** Rule 12: a reference to a whole `group_by` SLOT (every declared row, e.g.
 *  §8.1's 12 factors) renders rule 7's cell representation PER cohort column,
 *  reusing the exact component `ProjectionTableView` uses for the same data
 *  when it appears as a table cell instead. */
function GroupBySlotRepresentation({ value }: { value: ComputedSlotValue }) {
  const columns = (value['columns'] ?? []) as { value: string; label: string }[];
  const rows = (value['rows'] ?? []) as { label: string; field: string; cells: Record<string, unknown> }[];
  if (rows.length === 0) {
    return <p className="text-sm text-slate-600" data-testid="group-by-empty">No rows are declared for this distribution.</p>;
  }
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.field} data-testid="group-by-row" data-field={row.field}>
          <div className="text-sm font-medium text-slate-900">{row.label}</div>
          {columns.map((column) => {
            const cell = row.cells[column.value];
            return (
              <div key={column.value} className="mt-0.5" data-testid="group-by-column" data-column={column.value}>
                <span className="text-xs font-medium text-slate-600">{column.label}: </span>
                {isGroupByDistribution(cell) ? <GroupByCellRepresentation cell={cell} /> : cellText(cell)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** A resolved address that is none of the typed computed ops (`coverage`,
 *  `coverage.<group>`, `case_studies`, `case_studies.<group>`) is a plain
 *  nested count/rule payload, not one of the two structured cell shapes.
 *  Rendered as readable nested key/value text — never `JSON.stringify` — one
 *  level deep is enough for these shapes and no projection field is asserted
 *  to mean more than it does. */
function renderJsonValue(value: unknown): ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    return (
      <ul className="list-disc pl-5">
        {value.map((item, index) => <li key={index}>{renderJsonValue(item)}</li>)}
      </ul>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return '(none)';
  return (
    <dl className="pl-2">
      {entries.map(([key, val]) => (
        <div key={key} className="flex gap-2">
          <dt className="text-slate-500">{key}:</dt>
          <dd>{renderJsonValue(val)}</dd>
        </div>
      ))}
    </dl>
  );
}

function StructuredValueView({ value }: { value: unknown }) {
  return <div className="text-sm text-slate-800" data-testid="structured-value">{renderJsonValue(value)}</div>;
}

/** The human label for a resolved address: the value's own `label`/`title`
 *  when it carries one, else the address id itself (e.g. `coverage.documents`
 *  has neither, and the id is still meaningful). */
function addressLabel(id: string, value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    if (typeof v['label'] === 'string') return v['label'];
    if (typeof v['title'] === 'string') return v['title'];
  }
  return id;
}

/**
 * Rule 9: dispatch a resolved address to its shape's representation.
 * `slots` entries carry `op`; `tables`/`appendices` are rendered exactly as
 * `ProjectionTableView` renders them declared in the body (rule 7 cannot
 * drift between the two); everything else (`coverage`, `case_studies` and
 * their groups) is a plain structured payload.
 */
/**
 * Whole-branch review fix: a reference whose target is ALSO canonically
 * placed via its own `kind:"table"`/`kind:"appendix"` block renders a short
 * pointer to that section instead of a second full copy — up to 8 full
 * copies of `t6_2_incorporation_types` (its own block in §6.2, plus 7 prose
 * citations) and a second full 58-row `appendix_f` (its own block, plus
 * `p12_i_references`) is exactly what rules 23-28 exist to stop. Linked via
 * rule 23's anchor, so it is also navigable, not just shorter.
 */
function CanonicalPointer({ title, sectionId }: { title: string; sectionId: string }) {
  return (
    <p className="text-sm text-slate-700" data-testid="canonical-reference-pointer" data-target-section={sectionId}>
      Shown in full at{' '}
      <a href={`#${sectionAnchorId(sectionId)}`} className="text-indigo-700 underline">{title}</a>.
    </p>
  );
}

function addressContent(id: string, value: unknown, placed: CanonicalPlacements): ReactNode {
  if (value === undefined) return computedValueText(undefined);
  // Tables/appendices are checked BEFORE `isComputedSlotValue`: a
  // `ProjectionAppendix` also carries a string `op` (e.g. `column_projection`),
  // so the looser guard would otherwise misclassify it as a computed slot.
  if (isProjectionTable(value)) {
    // A reference is not a second declared placement — if this table
    // ALSO has its own `kind:"table"` block somewhere, that is where it
    // renders; this occurrence points there instead of repeating it.
    const canonicalSectionId = placed.tables.get(value.table_id);
    if (canonicalSectionId !== undefined) {
      return <CanonicalPointer title={value.title} sectionId={canonicalSectionId} />;
    }
    return (
      <ProjectionTableView
        testId={`computed-reference-${id}`}
        title={value.title}
        columns={value.columns}
        rows={value.rows.map((row) => ({ slotId: row.slot_id, cells: row.cells }))}
      />
    );
  }
  if (isProjectionAppendix(value)) {
    const canonicalSectionId = placed.appendices.get(value.appendix_id);
    if (canonicalSectionId !== undefined) {
      return <CanonicalPointer title={value.title} sectionId={canonicalSectionId} />;
    }
    // Rule 5: the same suppression an appendix gets wherever it is placed —
    // a reference to it is not a second, unfiltered view of the same data.
    return <AppendixView appendix={value} testId={`computed-reference-${id}`} />;
  }
  if (isReferenceEntry(value)) {
    // A curated guidance entry: a compact line, not the full `summary` —
    // the appendix (rule 5's own placement) is where the whole row lives.
    // The row label already shows the title; the link is HTTPS-only.
    const href = safeCompetitiveIntelUrl(value.url);
    return (
      <p className="text-sm text-slate-700" data-testid={`reference-entry-${id}`}>
        <span className="text-slate-500">{value.dates} · {value.status}</span>
        {href && (
          <>
            {' '}
            <a href={href} className="text-indigo-700 underline" target="_blank" rel="noreferrer">View source</a>
          </>
        )}
      </p>
    );
  }
  if (isComputedSlotValue(value)) {
    if (value.op === 'count_by_enum') return <CountByEnumRepresentation value={value} />;
    if (value.op === 'group_by') return <GroupBySlotRepresentation value={value} />;
    return computedValueText(value);
  }
  return <StructuredValueView value={value} />;
}

/** The body both a computed REFERENCE and a computed BLOCK render, so the
 *  "never a fabricated document citation" rule holds in one place for both,
 *  across every address shape (rule 9). The "no document is cited" disclosure
 *  is NOT rendered here — rule 13 requires it once per SLOT, not once per
 *  reference, and callers with different multiplicities own that. */
/**
 * Read-time display names for `drug:<id>` subject keys, resolved once by
 * `GET /report` (`agent_workstream_routes.py`, rule 18a's lookup) and shared
 * by every computed slot. A context rather than a prop because the computed
 * renderers sit six levels below the body, and the map is one value for the
 * whole report.
 */
const SubjectNamesContext = createContext<Record<string, string>>({});

/**
 * "90.4% (66/73 subjects)" states how many products a count counted, but not
 * WHICH — a reader who wants the 66 has to reconstruct them from a 125-row
 * appendix. The projection already knows: `count_units` reduces a distinct
 * subject set to its length, and `_Population.subject_keys` now returns that
 * same set, so this list can never disagree with the number above it.
 *
 * Collapsed by default: it is a drill-down, not part of the prose. A key that
 * resolved to no name renders as itself rather than being dropped, so the
 * count and the list always agree in length.
 */
function ComputedSubjectList({ value }: { value: unknown }) {
  const names = useContext(SubjectNamesContext);
  const keys = (value as { subject_keys?: unknown } | null)?.subject_keys;
  if (!Array.isArray(keys) || keys.length === 0) return null;
  const subjects = keys.filter((key): key is string => typeof key === 'string');
  if (subjects.length === 0) return null;
  return (
    <details className="mt-1" data-testid="computed-subject-list">
      <summary className="cursor-pointer text-xs text-indigo-700 hover:underline">
        Show the {subjects.length} {subjects.length === 1 ? 'product' : 'products'}
      </summary>
      <ul className="mt-1 ml-4 list-disc space-y-0.5 text-xs text-slate-700" data-testid="computed-subject-names">
        {subjects.map((key) => (
          <li key={key} data-subject-key={key}>{names[key] ?? key}</li>
        ))}
      </ul>
    </details>
  );
}

function ComputedSlotBody({ slotId, value, placed }: { slotId: string; value: unknown; placed: CanonicalPlacements }) {
  return (
    <>
      <div className="text-sm text-slate-800">{addressLabel(slotId, value)}</div>
      <div className="text-sm text-slate-700">{addressContent(slotId, value, placed)}</div>
      <ComputedSubjectList value={value} />
    </>
  );
}

const COMPUTED_ONLY_DISCLOSURE = 'Computed from the pinned analysis snapshot — no document is cited.';

/** Rule 13: one prose slot may cite several computed references; the
 *  disclosure renders on the LAST non-reference one only (a curated guidance
 *  entry is not computed from the snapshot), so it reads once per prose slot
 *  regardless of how many references that slot lists (was 117 times — once
 *  per occurrence). A slot with exactly one reference (today's common case)
 *  still carries it, unchanged. */
function ComputedReferenceRow({ slotId, value, showDisclosure, placed }: {
  slotId: string; value: unknown; showDisclosure: boolean; placed: CanonicalPlacements;
}) {
  return (
    <li
      className="border-l-2 border-indigo-200 pl-3 py-1"
      data-testid="computed-slot-reference"
      data-slot-id={slotId}
      data-resolved={value !== undefined ? 'true' : 'false'}
    >
      <ComputedSlotBody slotId={slotId} value={value} placed={placed} />
      {showDisclosure && (
        <div className="text-xs text-slate-500 mt-1" data-testid="computed-only-disclosure">{COMPUTED_ONLY_DISCLOSURE}</div>
      )}
    </li>
  );
}

/**
 * A computed count the TEMPLATE placed in the body — not a reference under
 * someone's prose. `oncology_pro_v1`'s "Objective" is five of these and no
 * prose at all; dropping them is what left that section empty.
 *
 * It carries its own testid so a declared body count and a prose citation stay
 * distinguishable, but resolves through the SAME typed renderer: it addresses
 * no document either, and must never become a `CitationRow`. It is always its
 * own single occurrence of its address (never one of several references under
 * one prose slot), so it always carries the disclosure itself.
 */
function ComputedBlockView({ slotId, value, placed }: { slotId: string; value: unknown; placed: CanonicalPlacements }) {
  return (
    <div
      className="border-l-2 border-indigo-200 pl-3 py-1"
      data-testid="computed-slot-block"
      data-slot-id={slotId}
      data-resolved={value !== undefined ? 'true' : 'false'}
    >
      <ComputedSlotBody slotId={slotId} value={value} placed={placed} />
      <div className="text-xs text-slate-500 mt-1" data-testid="computed-only-disclosure">{COMPUTED_ONLY_DISCLOSURE}</div>
    </div>
  );
}

function TemplatedSlotBlock({ slot, citations, projection, placed }: {
  slot: TemplatedSlot;
  citations: Map<string, ExcerptCitation>;
  projection: Record<string, unknown>;
  placed: CanonicalPlacements;
}) {
  // An excerpt id with no register entry is NOT rendered as a bare id and not
  // faked: the register is what makes a citation addressable.
  const excerpts = slot.excerpt_ids.map((id) => citations.get(id)).filter((c): c is ExcerptCitation => c !== undefined);
  const computed = slot.computed_slot_ids;
  // Rule 13's disclosure ("no document is cited") describes a COMPUTED value
  // — it does not describe a curated reference entry, which cites itself (its
  // own `url`). It attaches to the last id that is NOT a reference, so a
  // ref-only slot carries no disclosure at all, and a slot mixing computed
  // ids with references still shows it exactly once.
  let lastNonReferenceIndex = -1;
  for (let i = computed.length - 1; i >= 0; i--) {
    const id = computed[i];
    if (id !== undefined && !isReferenceEntry(projection[id])) { lastNonReferenceIndex = i; break; }
  }
  // A slot that declares a cardinality answers in `items`, and its `text`
  // defaults to "" (`_TemplateProseSlot`). Rendering `text` for one of those
  // renders nothing — the declared count is the slot's whole content.
  const items = slot.items ?? [];
  return (
    <div className="space-y-1" data-testid={`report-slot-${slot.slot_id}`} data-placeholder={slot.placeholder ? 'true' : 'false'}>
      {/* The placeholder's own text already discloses that it was not
          generated, and it is `supported` — a disclosure is not a defect, and
          it must not pick up an unsupported badge. */}
      {items.length > 0 ? (
        <ul className="list-disc pl-5 space-y-1" data-testid={`report-slot-items-${slot.slot_id}`}>
          {items.map((item, index) => (
            <li key={index} className="text-sm text-slate-800"><ReportText text={item} /></li>
          ))}
        </ul>
      ) : (
        <ReportText text={slot.text} />
      )}
      {!slot.supported && (
        <p className="text-sm text-amber-900 font-medium" data-testid="unsupported-slot">
          (unsupported{slot.flag_reason ? `: ${slot.flag_reason}` : ''})
        </p>
      )}
      {(excerpts.length > 0 || computed.length > 0) && (
        <ul className="mt-1 space-y-1">
          {excerpts.map((citation) => <CitationRow key={citation.excerpt_id} citation={citation} />)}
          {computed.map((slotId, index) => (
            <ComputedReferenceRow
              key={slotId}
              slotId={slotId}
              value={projection[slotId]}
              showDisclosure={index === lastNonReferenceIndex}
              placed={placed}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return '—';
  if (typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean') return String(cell);
  return JSON.stringify(cell);
}

/** Rule 22: local DISPLAY formatting only — never a `pro_instruments` catalog
 *  lookup (it is keyed on values like `"EQ-5D"`, not on these extraction
 *  keys, so a lookup would silently rename some instruments and not others).
 *  An already-human value (no underscore) passes through unchanged. A
 *  two-segment key reads naturally with a hyphen (`EQ_5D` -> `EQ-5D`). A
 *  longer key is presented as the several underscore-delimited components it
 *  visibly concatenates, not asserted to be one instrument's name. */
function formatInstrumentKey(raw: string): string {
  if (!raw.includes('_')) return raw;
  const segments = raw.split('_').filter(Boolean);
  if (segments.length <= 2) return segments.join('-');
  return segments.join(' / ');
}

const INSTRUMENT_COLUMN_HEADERS = new Set(['PRO Instrument', 'PRO']);

/**
 * Rule 27's declared typographic scale — one lookup, shared by every heading
 * level in the templated body (section headings, table/appendix titles).
 * Sizes/weights are the plan's declared values (Task 8): L1 20/700, L2
 * 17/700, L3 15/600, L4 14/600 (secondary color token), L5-L6 13/600
 * uppercase with wide tracking. Keyed on the TEMPLATE's own `section.level`
 * (1-6), independent of the semantic `h${...}` tag a heading renders as.
 * `break-after-avoid` on every level (rule 31, fix round 1 P2-1): harmless on
 * screen — it only has an effect under pagination — so it belongs on the
 * shared scale rather than duplicated at every call site.
 */
function headingClasses(level: number): string {
  switch (level) {
    case 1: return 'text-[20px] font-bold text-slate-900 break-after-avoid';
    case 2: return 'text-[17px] font-bold text-slate-900 break-after-avoid';
    case 3: return 'text-[15px] font-semibold text-slate-900 break-after-avoid';
    case 4: return 'text-[14px] font-semibold text-slate-600 break-after-avoid';
    default: return 'text-[13px] font-semibold uppercase tracking-wide text-slate-500 break-after-avoid';
  }
}

/**
 * Rule 27, fix round 1 (P2-3): tabular figures must reach every cell that
 * actually carries numbers, not only a raw JS `number`. A `%` cell is
 * ALWAYS a string (`"12.5% (3/24 subjects)"`, `projection.py:_percentage`),
 * and a `count_by_enum`/`group_by`/`_distribution` cell is a structured
 * object whose OWN sub-renderer prints several numbers — `font-variant-
 * numeric` is inherited, so applying the class at the `<td>` still reaches
 * every number those sub-renderers print, with no change needed there.
 */
function isNumericCellContent(cell: unknown): boolean {
  return typeof cell === 'number' ||
    isPercentageDisplayString(cell) ||
    isComputedSlotValue(cell) ||
    isGroupByDistribution(cell);
}

/** Rule 7 + 16 + 22 dispatcher for one table/appendix cell: the two
 *  structured shapes render through the shared components above (never
 *  `JSON.stringify`); an empty array gets an explicit marker, never `[]`; a
 *  circular-percentage row's percentage cell states the construction instead
 *  of the bare (misleading) `100.0% (N/N units)` string; an instrument-key
 *  column gets rule 22's display formatting; everything else is `cellText`,
 *  unchanged. */
function renderCellContent(cell: unknown, column?: string, circularPercentageRow?: boolean): ReactNode {
  if (circularPercentageRow && isPercentageDisplayString(cell)) return CIRCULAR_PERCENTAGE_CELL_NOTE;
  if (isComputedSlotValue(cell)) {
    if (cell.op === 'count_by_enum') return <CountByEnumRepresentation value={cell} />;
    if (cell.op === 'group_by') return <GroupBySlotRepresentation value={cell} />;
  }
  if (isGroupByDistribution(cell)) return <GroupByCellRepresentation cell={cell} />;
  if (Array.isArray(cell)) {
    if (cell.length === 0) return <span data-testid="empty-array-marker">(none)</span>;
    return cell.map((item) => cellText(item)).join(', ');
  }
  if (typeof cell === 'string' && column && INSTRUMENT_COLUMN_HEADERS.has(column)) return formatInstrumentKey(cell);
  return cellText(cell);
}

/** One row of a rendered table. `slotId` is the row's originating computed
 *  slot (`ProjectionTable.rows[].slot_id`) when it has one — `null` for an
 *  appendix row, which is a column-projection over records, not a slot.
 *  Rule 16 needs this identity preserved through rendering: earlier, mapping
 *  straight to `row.cells` discarded it, so the circular-percentage check
 *  never ran on the table-cell path at all. */
interface ProjectionTableRow {
  slotId: string | null;
  cells: unknown[];
}

function ProjectionTableView({ testId, title, columns, rows }: {
  testId: string;
  /** Omitted when the caller already renders its own heading for this table
   *  (`AppendixView`'s collapsible header) — otherwise this and that heading
   *  would both carry the same accessible name. */
  title?: string;
  columns: string[];
  rows: ProjectionTableRow[];
}) {
  return (
    // Rule 26: overflow is confined to THIS container, never the page body —
    // `print:overflow-visible` because a scrollbar cannot be followed on
    // paper (rule 31): what does not fit must wrap, never clip at the page
    // edge.
    <div className="min-w-0 overflow-x-auto print:overflow-visible" data-testid={testId}>
      {title && <h4 className={headingClasses(4)}>{title}</h4>}
      <table className="mt-1 text-sm w-full">
        <thead>
          <tr>{columns.map((column) => <th key={column} className="pr-4 text-left font-medium text-slate-700">{column}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const circular = row.slotId !== null && CIRCULAR_PERCENTAGE_SLOT_IDS.has(row.slotId);
            return (
              // Rule 31: a normal-sized row is never split across a page break.
              <tr key={index} className="break-inside-avoid">{row.cells.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  // Rule 26: a bounded max-width plus wrapping keeps one long
                  // cell from stretching the table past the page width in the
                  // common case; rule 27: numeric cells use tabular figures so
                  // columns of numbers align.
                  className={`pr-4 align-top text-slate-800 max-w-xs break-words ${isNumericCellContent(cell) ? 'tabular-nums' : ''}`}
                >
                  {renderCellContent(cell, columns[cellIndex], circular)}
                </td>
              ))}</tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The DECLARED body (`report_writer.report_sections`) — blocks, not prose.
//
// Prose is not the body. `oncology_pro_v1`'s "Objective" is fixed text plus
// five computed counts and NOT ONE prose slot, so a renderer that walked the
// section spine and emitted the written prose under each heading left that
// section numbered, titled and EMPTY. Every declared block is rendered here,
// each through the renderer its kind requires.
// ---------------------------------------------------------------------------

/** The body to walk: `sections` when the envelope carries it, else the
 *  block-free spine (a body written before `report_sections` landed), which
 *  still renders its prose exactly as it used to. */
function declaredSections(body: TemplatedReportBody): TemplatedBodySection[] {
  if (Array.isArray(body.sections) && body.sections.length > 0) return body.sections;
  return (body.section_tree ?? []).map((node) => ({ ...node, repeat: null, subjects: [], blocks: [] }));
}

/** A repeating section renders its whole block list once per subject; every
 *  other section renders it once, with no subject. */
function sectionSubjects(section: TemplatedBodySection): (TemplatedSubject | null)[] {
  return section.repeat ? section.subjects : [null];
}

/**
 * The written slots a `prose` block resolves to.
 *
 * The match is on `template_slot_id` — the DECLARATION — because a repetition's
 * own `slot_id` is `"<template_slot_id>#<subject_key>"` and would match no
 * block. Under a subject, the repetition for THAT subject is the one that
 * belongs in this block; the others belong to the other subjects' copies.
 */
function proseSlotsFor(
  slots: TemplatedSlot[],
  templateSlotId: string,
  subject: TemplatedSubject | null,
): TemplatedSlot[] {
  return slots.filter((slot) =>
    (slot.template_slot_id || slot.slot_id) === templateSlotId &&
    (subject === null || (slot.subject_key ?? null) === subject.subject_key));
}

/** A block the template declared and the pinned artifacts do not contain.
 *  Disclosed, never dropped: a silently missing declared block is exactly the
 *  defect this body view exists to stop. */
function MissingBlock({ kind, id }: { kind: string; id: string }) {
  return (
    <p className="text-sm text-slate-600" data-testid="report-missing-block" data-block-kind={kind} data-block-id={id}>
      This {kind} ({id}) is not present in the pinned report.
    </p>
  );
}

/**
 * Rule 1: the template's own placeholder predicate — the WHOLE trimmed value
 * is one bracketed placeholder, with an optional leading list bullet. A block
 * that mixes a bracket span into real prose (or carries text outside the
 * brackets) is not one of these — the predicate is whole-value by
 * construction, never a substring search.
 */
const PLACEHOLDER_TEXT_PATTERN = /^-?\s*\[[^\]]*\]$/;

function isPlaceholderTextBlock(block: TemplatedBlock): boolean {
  return block.kind === 'text' && PLACEHOLDER_TEXT_PATTERN.test(block.value.trim());
}

/**
 * Rule 3's support test for one `field` block, reusing rule 18a's resolution
 * order (the same one `FieldBlockView` renders): the subject's read-time
 * `drug_name` takes precedence over the analysis record's own field for
 * `product_name`; every other field reads straight from `subject.fields`.
 * A block with no subject context (should not occur — `field` blocks only
 * appear in repeating sections) is never supported.
 */
function fieldBlockSupported(block: TemplatedBlock, subject: TemplatedSubject | null): boolean {
  if (!subject) return false;
  const resolvedDisplay = block.value === 'product_name' ? (subject.drug_name ?? null) : null;
  const rawValue = subject.fields[block.value];
  const value = resolvedDisplay ?? rawValue;
  return value !== null && value !== undefined;
}

/**
 * Rule 4's identity test for a whole case-study subject. THE COUPLING: this
 * must use the SAME resolution order as `fieldBlockSupported`'s
 * `product_name` case — a subject Task 5 recovered a display name for
 * (`drug_name`) is attributable even when the analysis record's own
 * `fields.product_name` is unsupported, and must not be dropped by checking
 * the raw field alone.
 */
function subjectIdentitySupported(subject: TemplatedSubject): boolean {
  const identity = subject.drug_name ?? subject.fields['product_name'] ?? null;
  return identity !== null && identity !== undefined;
}

/**
 * One `field` block of a case study.
 *
 * Rule 3: an unsupported value is OMITTED — the caller (`computeSectionOmissions`)
 * counts it and the section's one composite note (rule 8) discloses the count.
 * This is no longer the disclosure surface itself: a per-line "not supported by
 * the pinned snapshot" would be exactly the per-item noise rule 8 replaces.
 *
 * Rule 18a: the `product_name` field — what a "Case Study:" heading names —
 * prefers the subject's read-time-resolved `drug_name` over the analysis
 * record's own (possibly unsupported) `fields.product_name`, the same
 * decoration the report route already applies to citation display names.
 *
 * Rule 22: `pro_instrument` is a raw extraction key (`EQ_5D`, a composite of
 * concatenated codes) and gets local display formatting, never a catalog
 * lookup.
 */
function FieldBlockView({ block, subject }: { block: TemplatedBlock; subject: TemplatedSubject | null }) {
  if (!fieldBlockSupported(block, subject)) return null;
  const resolvedDisplay = block.value === 'product_name' ? (subject?.drug_name ?? null) : null;
  const rawValue = subject ? subject.fields[block.value] : undefined;
  const value = resolvedDisplay ?? rawValue;
  const display = block.value === 'pro_instrument' && typeof value === 'string'
    ? formatInstrumentKey(value)
    : cellText(value);
  return (
    <div
      className="text-sm text-slate-800"
      data-testid="report-field"
      data-field={block.value}
      data-supported="true"
    >
      <span className="font-medium">{block.label ?? block.value}:</span> {display}
    </div>
  );
}

interface AppendixDisplay {
  columns: string[];
  rows: unknown[][];
  omittedRowCount: number;
  omittedColumns: string[];
  totalRows: number;
}

/**
 * Rule 5 + 7 (whole-column suppression): an appendix omits every row whose
 * cells are ALL null, then omits every column null in 100% of the REMAINING
 * (rendered) rows — never the original row set, so a column that is only
 * null in the row rule 5 already dropped is not falsely counted supported.
 * A row with at least one supported value is kept, cells and all.
 */
function filterAppendixDisplay(appendix: ProjectionAppendix): AppendixDisplay {
  const totalRows = appendix.rows.length;
  const keptRows = appendix.rows.filter((row) => row.some((cell) => cell !== null && cell !== undefined));
  const keepColumn = appendix.columns.map((_, colIndex) =>
    keptRows.some((row) => row[colIndex] !== null && row[colIndex] !== undefined));
  const columns = appendix.columns.filter((_, index) => keepColumn[index]);
  const omittedColumns = appendix.columns.filter((_, index) => !keepColumn[index]);
  const rows = keptRows.map((row) => row.filter((_, index) => keepColumn[index]));
  return { columns, rows, omittedRowCount: totalRows - keptRows.length, omittedColumns, totalRows };
}

/** Rule 25's declared pagination threshold: an expanded appendix paginates
 *  above this many rows (the worked case is Appendix D's 125 rows -> 3
 *  pages). */
const APPENDIX_PAGE_SIZE = 50;

/**
 * Renders one appendix wherever it appears — a declared body block, the
 * trailing "not placed by any block" region, or a computed reference — so
 * rule 5's suppression cannot drift between the three. Rule 8: "never an
 * empty table" — when every row is omitted, only the header renders; the
 * section's composite note (not this component) carries the disclosure. It
 * is never rendered here either: rule 8's note stays with the CALLER (the
 * section, or the trailing region), outside this component and outside its
 * collapsed/expanded state, so it is visible whichever state the appendix is
 * in — this component's own header carries only the row/column COUNTS.
 *
 * Rule 25: collapsed by default, showing the true row/column counts on the
 * collapsed header without paying for the render. An expanded appendix
 * paginates above `APPENDIX_PAGE_SIZE` rows.
 */
function AppendixView({ appendix, testId }: { appendix: ProjectionAppendix; testId: string }) {
  const display = filterAppendixDisplay(appendix);
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);

  // Rule 25's counts, rule 31's PRINTED disclosure of them: the true declared
  // size, stated identically whether or not there is anything to expand.
  // fix round 1 (P2-1): this must never be a descendant of the print:hidden
  // toggle, or a collapsed appendix prints only its title.
  const counts = (
    <span className="shrink-0 text-xs font-normal text-slate-500" data-testid={`${testId}-counts`}>
      {display.totalRows} row{display.totalRows === 1 ? '' : 's'} &middot; {appendix.columns.length} column{appendix.columns.length === 1 ? '' : 's'}
    </span>
  );

  // fix round 1 (P2-4): a fully-omitted appendix (every row suppressed) has
  // nothing to expand — rule 8 forbids ever rendering an empty table — but it
  // still owes the reader its true declared counts, exactly like every other
  // appendix. Only the interactive toggle is skipped, since there is nothing
  // for it to reveal.
  if (display.rows.length === 0) {
    return (
      <div data-testid={testId}>
        <div className="flex w-full items-center justify-between gap-3">
          <h4 className={headingClasses(4)}>{appendix.title}</h4>
          {counts}
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(display.rows.length / APPENDIX_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageRows = display.rows.slice(currentPage * APPENDIX_PAGE_SIZE, (currentPage + 1) * APPENDIX_PAGE_SIZE);
  const firstRowNumber = currentPage * APPENDIX_PAGE_SIZE + 1;
  const lastRowNumber = currentPage * APPENDIX_PAGE_SIZE + pageRows.length;

  return (
    <div data-testid={testId} data-expanded={expanded ? 'true' : 'false'}>
      <div className="flex w-full items-center justify-between gap-3">
        {/* Always present, regardless of collapse state — a reader (and
            print, rule 31) sees the title and true counts without expanding. */}
        <h4 className={headingClasses(4)}>{appendix.title}</h4>
        <div className="flex shrink-0 items-center gap-2">
          {counts}
          {/* fix round 1 (P2-1): ONLY the click affordance is print:hidden
              now — the counts above it are not, so a collapsed appendix
              still prints "explicitly listed as omitted with accurate
              counts" instead of just its bare title. */}
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            data-testid={`${testId}-toggle`}
            className="text-xs font-normal text-slate-500 underline print:hidden"
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>
      {expanded && (
        <>
          <ProjectionTableView
            testId={`${testId}-table`}
            columns={display.columns}
            rows={pageRows.map((cells) => ({ slotId: null, cells }))}
          />
          {totalPages > 1 && (
            <div className="mt-1 flex items-center gap-3 text-xs text-slate-600" data-testid={`${testId}-pagination`}>
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(0, value - 1))}
                disabled={currentPage === 0}
                className="disabled:opacity-40 print:hidden"
              >
                Previous
              </button>
              {/* fix round 1 (P2-1): kept OUT of print:hidden and now states
                  the shown row range against the true total — printing only
                  `pageRows` must never look like the whole appendix, and
                  hiding this text (as before) is exactly what let it. */}
              <span data-testid={`${testId}-page-status`}>
                Page {currentPage + 1} of {totalPages} (rows {firstRowNumber}&ndash;{lastRowNumber} of {display.rows.length})
              </span>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}
                disabled={currentPage >= totalPages - 1}
                className="disabled:opacity-40 print:hidden"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SectionBlockView({ block, subject, body, citations, projectionSlots, placed }: {
  block: TemplatedBlock;
  subject: TemplatedSubject | null;
  body: TemplatedReportBody;
  citations: Map<string, ExcerptCitation>;
  projectionSlots: Record<string, unknown>;
  placed: CanonicalPlacements;
}) {
  switch (block.kind) {
    case 'text':
      // Rule 1: the template's own fixed wording renders as prose — UNLESS the
      // whole value is one bracketed placeholder (optional leading bullet),
      // which is omitted. The caller counts it for the section's composite
      // note (rule 8); this is not a second disclosure surface.
      if (isPlaceholderTextBlock(block)) return null;
      return <ReportText text={block.value} testId="report-block-text" />;
    case 'computed':
      return <ComputedBlockView slotId={block.value} value={projectionSlots[block.value]} placed={placed} />;
    case 'table': {
      const table = body.projection?.tables?.[block.value];
      return table
        ? <ProjectionTableView
            testId={`report-table-${table.table_id}`}
            title={table.title}
            columns={table.columns}
            rows={table.rows.map((row) => ({ slotId: row.slot_id, cells: row.cells }))}
          />
        : <MissingBlock kind="table" id={block.value} />;
    }
    case 'appendix': {
      const appendix = body.projection?.appendices?.[block.value];
      return appendix
        ? <AppendixView appendix={appendix} testId={`report-appendix-${appendix.appendix_id}`} />
        : <MissingBlock kind="appendix" id={block.value} />;
    }
    case 'field':
      return <FieldBlockView block={block} subject={subject} />;
    case 'figure':
      // Rule 2: a figure is not a claim and its absence changes no number —
      // omitted unconditionally. The caller counts it for the section's
      // composite note; a figure is never disclosed inline any more.
      return null;
    case 'prose': {
      const written = proseSlotsFor(body.slots, block.value, subject);
      if (written.length === 0) return <MissingBlock kind="prose slot" id={block.value} />;
      return (
        <div className="space-y-1">
          {/* The template's own label for this block ("FDA Evaluation", "Key
              PRO Evidence"): declared content, not decoration. */}
          {block.label && (
            <div className="text-sm font-semibold text-slate-900" data-testid="report-block-label">{block.label}</div>
          )}
          {written.map((slot) => (
            <TemplatedSlotBlock key={slot.slot_id} slot={slot} citations={citations} projection={projectionSlots} placed={placed} />
          ))}
        </div>
      );
    }
    default:
      return null;
  }
}

/**
 * What the DECLARED body already placed. A table or appendix a block placed
 * must not render a second time in the trailing region, and a slot a prose
 * block placed is not "outside the template's sections".
 *
 * `tables`/`appendices` map id -> the CANONICAL section_id that placed it
 * (its own `kind:"table"`/`kind:"appendix"` block — never a `computed`
 * block or a prose reference, which are always reference sites, whatever
 * they point at). Whole-branch review fix: a table or appendix reached
 * ALSO by reference (a `computed` block, or a prose slot's
 * `computed_slot_ids`) needs to know WHERE its canonical copy lives, so a
 * reference site can point there instead of re-rendering the full content —
 * `t6_2_incorporation_types` is canonically placed once in §6.2 and cited by
 * 7 other prose slots; without this, it rendered in full 8 times.
 */
function placedByBody(body: TemplatedReportBody, sections: TemplatedBodySection[]) {
  const slots = new Set<string>();
  const tables = new Map<string, string>();
  const appendices = new Map<string, string>();
  for (const section of sections) {
    if (section.blocks.length === 0) {
      // Spine-only fallback: a section places every slot written FOR it.
      for (const slot of body.slots) {
        if (slot.section_id === section.section_id) slots.add(slot.slot_id);
      }
      continue;
    }
    for (const block of section.blocks) {
      if (block.kind === 'table') tables.set(block.value, section.section_id);
      else if (block.kind === 'appendix') appendices.set(block.value, section.section_id);
      else if (block.kind === 'prose') {
        for (const subject of sectionSubjects(section)) {
          for (const slot of proseSlotsFor(body.slots, block.value, subject)) slots.add(slot.slot_id);
        }
      }
    }
  }
  return { slots, tables, appendices };
}

/** The subset of `placedByBody`'s result a reference-rendering site needs:
 *  where (if anywhere) a table/appendix id is CANONICALLY placed. Narrower
 *  than the full return type on purpose, so every caller threading this
 *  through several component layers states exactly what it depends on. */
interface CanonicalPlacements {
  tables: Map<string, string>;
  appendices: Map<string, string>;
}

/**
 * The declared body in template order, then whatever the body did NOT place.
 *
 * A table or appendix the template placed renders INSIDE its section, because
 * `report_sections` says which section declared it. The trailing regions carry
 * only what nothing placed — on a spine-only body (no blocks) that is still
 * every table and every appendix, exactly as before.
 */

/**
 * Rule 9: the FULL address space a computed reference (or a `computed`
 * block) may resolve against — the same one `report_writer.
 * projection_addresses` (`backend/app/agents/report_writer.py:243-266`) gave
 * the model. `slots`/`tables`/`appendices`/`references` are flat id maps
 * (`references` keyed by `ref_id` — the curated-guidance address space,
 * `_PROJECTION_ID_MAP_SECTIONS`); `coverage` and `case_studies` are each
 * addressable both as a whole and via each of their second-level groups
 * (`coverage.documents`, `case_studies.cohorts`, …). Resolving against
 * `slots` alone (HEAD) is what left 21 legitimate references unresolved.
 */
function projectionAddresses(projection: ReportProjection | undefined): Record<string, unknown> {
  const addresses: Record<string, unknown> = {};
  if (!projection) return addresses;
  const idMaps: (Record<string, unknown> | undefined)[] = [
    projection.slots as unknown as Record<string, unknown>,
    projection.tables as unknown as Record<string, unknown>,
    projection.appendices as unknown as Record<string, unknown>,
    projection.results as unknown as Record<string, unknown>,
    projection.references as unknown as Record<string, unknown>,
  ];
  for (const group of idMaps) {
    for (const [id, value] of Object.entries(group ?? {})) addresses[id] = value;
  }
  const structuredSections: [string, unknown][] = [
    ['coverage', projection.coverage],
    ['case_studies', projection.case_studies],
  ];
  for (const [section, sectionBody] of structuredSections) {
    if (sectionBody === null || sectionBody === undefined) continue;
    addresses[section] = sectionBody;
    if (typeof sectionBody === 'object' && !Array.isArray(sectionBody)) {
      for (const [group, value] of Object.entries(sectionBody as Record<string, unknown>)) {
        addresses[`${section}.${group}`] = value;
      }
    }
  }
  return addresses;
}

/** One appendix's contribution to its section's composite note (rule 8): how
 *  many of its rows were omitted (of how many declared), which columns were
 *  omitted, and — rule 6 — the subject count, stated whenever it differs from
 *  the rendered row count (an appendix collapses duplicate tuples upstream,
 *  so 9 rows can stand for 73 products). */
interface AppendixNoteEntry {
  id: string;
  title: string;
  omittedRows: number;
  totalRows: number;
  omittedColumns: string[];
  subjectsNote?: { rows: number; subjects: number };
}

interface SectionOmissions {
  textOmitted: number;
  figureOmitted: number;
  fieldOmitted: number;
  subjectOmitted: number;
  appendixNotes: AppendixNoteEntry[];
}

/**
 * Rule 5/6's per-appendix note contribution, computed once and shared by
 * every caller that needs to know whether an appendix has anything to
 * disclose — a declared `appendix` block, a computed reference to one
 * (`case 'computed'`, or a `computed_slot_ids` entry under a `prose` block),
 * and the trailing "not placed by any block" region. `null` when the
 * appendix filtered clean (no row/column omitted, no subject-count
 * mismatch) — nothing to say, so no entry.
 */
function computeAppendixNoteEntry(appendix: ProjectionAppendix): AppendixNoteEntry | null {
  const display = filterAppendixDisplay(appendix);
  const subjects = appendix.counts?.['subjects'];
  const subjectsNote = typeof subjects === 'number' && subjects !== display.rows.length
    ? { rows: display.rows.length, subjects } : null;
  if (display.omittedRowCount === 0 && display.omittedColumns.length === 0 && !subjectsNote) return null;
  return {
    id: appendix.appendix_id, title: appendix.title,
    omittedRows: display.omittedRowCount, totalRows: display.totalRows,
    omittedColumns: display.omittedColumns,
    ...(subjectsNote ? { subjectsNote } : {}),
  };
}

/**
 * Every appendix id a section's declared blocks could resolve — not just a
 * `kind: 'appendix'` block, but a `computed` block (`ComputedBlockView`
 * resolves the same widened address space) and every `computed_slot_ids`
 * entry of a `prose` block's WRITTEN slots for this section's kept subjects.
 * An id that does not name an appendix is harmless here; the caller looks it
 * up in `projection.appendices` and skips a miss. Without this, an appendix
 * reached only by reference (never placed as its own block) suppressed rows
 * and columns with no disclosure anywhere on the page.
 */
function appendixIdsInSection(
  section: TemplatedBodySection,
  body: TemplatedReportBody,
  keptSubjects: (TemplatedSubject | null)[],
  placedAppendices: Map<string, string>,
): Set<string> {
  const ids = new Set<string>();
  for (const block of section.blocks) {
    if (block.kind === 'appendix') {
      // The CANONICAL placement: this section's own note is where this
      // appendix's disclosure belongs, unconditionally.
      ids.add(block.value);
    } else if (block.kind === 'computed') {
      // Whole-branch review fix: a `computed` block is a REFERENCE. If its
      // target is canonically placed elsewhere (own `kind:"appendix"` block
      // somewhere in the body — including this same section, already added
      // above), it renders as a pointer (`addressContent`), not a second
      // full view, so its omission disclosure belongs solely to the
      // canonical section and must not also fire here.
      if (!placedAppendices.has(block.value)) ids.add(block.value);
    } else if (block.kind === 'prose') {
      for (const subject of keptSubjects) {
        for (const slot of proseSlotsFor(body.slots, block.value, subject)) {
          for (const id of slot.computed_slot_ids) {
            if (!placedAppendices.has(id)) ids.add(id);
          }
        }
      }
    }
  }
  return ids;
}

/**
 * One pass over a declared section computing everything rule 8's composite
 * note needs — never a per-item disclosure, exactly the counts and reasons.
 * Field-line omissions are counted only for KEPT subjects (rule 4's whole
 * subjects are their own item, never double-counted through their field
 * lines) — the same separation rules 3 and 4 keep at render time.
 */
function computeSectionOmissions(
  section: TemplatedBodySection,
  body: TemplatedReportBody,
  placedAppendices: Map<string, string>,
): SectionOmissions {
  const rawSubjects = sectionSubjects(section);
  const keptSubjects = section.repeat
    ? rawSubjects.filter((subject): subject is TemplatedSubject => subject !== null && subjectIdentitySupported(subject))
    : rawSubjects;
  const subjectOmitted = section.repeat ? rawSubjects.length - keptSubjects.length : 0;

  let textOmitted = 0;
  let figureOmitted = 0;
  let fieldOmitted = 0;
  for (const subject of keptSubjects) {
    for (const block of section.blocks) {
      if (isPlaceholderTextBlock(block)) textOmitted++;
      else if (block.kind === 'figure') figureOmitted++;
      else if (block.kind === 'field' && !fieldBlockSupported(block, subject)) fieldOmitted++;
    }
  }

  const appendixNotes: AppendixNoteEntry[] = [];
  for (const id of appendixIdsInSection(section, body, keptSubjects, placedAppendices)) {
    const appendix = body.projection?.appendices?.[id];
    if (!appendix) continue;
    const entry = computeAppendixNoteEntry(appendix);
    if (entry) appendixNotes.push(entry);
  }

  return { textOmitted, figureOmitted, fieldOmitted, subjectOmitted, appendixNotes };
}

/** One appendix's clause inside a composite note (rule 8's per-appendix "N of
 *  M rows omitted" + column names, plus rule 6's subject-count sentence when
 *  it applies). Shared between `SectionOmissionNote` (a declared section) and
 *  `TrailingAppendixNote` (appendices no block placed), so the exact same
 *  disclosure renders regardless of where the appendix landed. Rule 6's
 *  count is stated even when nothing was actually omitted (a fully-supported
 *  appendix whose declared subject count still differs from its row count) —
 *  it is a disclosure required unconditionally, never gated on rule 5 having
 *  fired too. */
function AppendixNoteClause({ entry }: { entry: AppendixNoteEntry }) {
  const hasRowOrColumnOmission = entry.omittedRows > 0 || entry.omittedColumns.length > 0;
  return (
    <span data-testid="report-section-note-appendix" data-appendix-id={entry.id}>
      {hasRowOrColumnOmission && (
        <>
          {' '}{entry.title}: {entry.omittedRows} of {entry.totalRows} rows omitted
          {entry.omittedColumns.length > 0 ? `; omitted columns: ${entry.omittedColumns.join(', ')}` : ''}.
        </>
      )}
      {entry.subjectsNote && (
        <>
          {' '}{hasRowOrColumnOmission ? '' : `${entry.title}: `}
          {entry.subjectsNote.subjects} subjects are represented by these {entry.subjectsNote.rows} rows.
        </>
      )}
    </span>
  );
}

/**
 * Rule 8: exactly ONE composite note per section, rendered LAST. Rules 4-6
 * contribute clauses here rather than emitting notes of their own — this is
 * the only place any of the four counts above ever reaches the reader. A
 * section that omitted nothing renders none.
 *
 * The gate is `totalItems === 0 AND appendixNotes.length === 0`, not
 * `totalItems === 0` alone: rule 6's subject-count sentence is a required
 * disclosure but is not itself a counted "item" (rule 8's item list is text/
 * figure/field/subject/row/column — a mismatch is none of those), so an
 * appendix whose ONLY finding is a subject-count mismatch (zero rows/columns
 * actually omitted) must still surface a note. Gating on `totalItems` alone
 * discarded exactly that note.
 */
/** Rule 8's per-item count contributed by a set of appendix note entries —
 *  one item per omitted row, plus one per omitted column. Shared by
 *  `SectionOmissionNote` and `TrailingAppendixNote` so the two composite
 *  notes count identically regardless of where the appendix landed. */
function appendixItemsOmitted(entries: AppendixNoteEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.omittedRows + entry.omittedColumns.length, 0);
}

function SectionOmissionNote({ omissions }: { omissions: SectionOmissions }) {
  const { textOmitted, figureOmitted, fieldOmitted, subjectOmitted, appendixNotes } = omissions;
  const totalItems = textOmitted + figureOmitted + fieldOmitted + subjectOmitted + appendixItemsOmitted(appendixNotes);
  if (totalItems === 0 && appendixNotes.length === 0) return null;
  return (
    <p className="text-xs text-slate-600 mt-2" data-testid="report-section-note">
      {totalItems > 0 && (
        <>
          {totalItems} item{totalItems === 1 ? '' : 's'} omitted from this section because they carried no
          information the pinned snapshot supports.
        </>
      )}
      {subjectOmitted > 0 && (
        <span data-testid="report-section-note-case-studies">
          {' '}{subjectOmitted} case {subjectOmitted === 1 ? 'study' : 'studies'} omitted: product could not
          be identified.
        </span>
      )}
      {appendixNotes.map((entry) => <AppendixNoteClause key={entry.id} entry={entry} />)}
    </p>
  );
}

/**
 * Rule 8's counterpart for appendices no declared block ever placed (the
 * trailing region below every numbered section). These are not inside any
 * `TemplatedBodySection`, so `SectionOmissionNote` never sees them — without
 * this, an all-null trailing appendix rendered its header with no row/column
 * disclosure anywhere on the page, which is exactly the silent-omission
 * failure this task exists to close. Renders nothing when every trailing
 * appendix filtered clean.
 *
 * fix round 1 (P2-7, Task 7 residual): this note used to render only the
 * per-appendix clauses, never rule 8's leading TOTAL item count (one item
 * per omitted row or column) that `SectionOmissionNote` always states —
 * two all-null rows and two omitted columns disclosed their own removal but
 * the note never said "4 items omitted". Still exactly ONE composite note
 * (rule 8): the total is added to this same `<p>`, not a second note.
 */
function TrailingAppendixNote({ appendices }: { appendices: ProjectionAppendix[] }) {
  const entries = appendices
    .map((appendix) => computeAppendixNoteEntry(appendix))
    .filter((entry): entry is AppendixNoteEntry => entry !== null);
  if (entries.length === 0) return null;
  const totalItems = appendixItemsOmitted(entries);
  return (
    <p className="text-xs text-slate-600 mt-2" data-testid="report-section-note">
      {totalItems > 0 && (
        <>
          {totalItems} item{totalItems === 1 ? '' : 's'} omitted from this report because they carried no
          information the pinned snapshot supports.
        </>
      )}
      {entries.map((entry) => <AppendixNoteClause key={entry.id} entry={entry} />)}
    </p>
  );
}

/**
 * Rule 23: a stable, unique anchor id derived from `section_id` alone — NEVER
 * from the title, which is neither unique (two sections can share a title)
 * nor stable (a title can be edited without the section's identity moving).
 * Reused verbatim as the existing `data-testid`, so it inherits that id's
 * uniqueness for free.
 */
function sectionAnchorId(sectionId: string): string {
  return `report-section-${sectionId}`;
}

/** One top-level section and every section that follows it up to (not
 *  including) the next top-level one — rule 24's collapsible navigation
 *  groups. Sections arrive in template order, so a single forward pass over
 *  the flat list is sufficient; nothing needs the block content. */
interface NavGroup {
  top: TemplatedBodySection;
  members: TemplatedBodySection[];
}

function buildNavGroups(sections: TemplatedBodySection[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let current: NavGroup | null = null;
  for (const section of sections) {
    if (!current || section.level <= 1) {
      current = { top: section, members: [] };
      groups.push(current);
    }
    current.members.push(section);
  }
  return groups;
}

/**
 * Rule 23's table of contents + rule 24's persistent navigation, built as one
 * component: a sticky/fixed index, collapsible by top-level section, that
 * marks the section currently in view. Every entry links to an anchor rule
 * 23 requires to already be present on that section (`sectionAnchorId`), so
 * "every TOC entry resolves to a section in the body" holds by construction.
 *
 * Active-section tracking degrades gracefully with no `IntersectionObserver`
 * (jsdom does not implement one — every existing lazy-load feature in this
 * app stubs it PER-TEST rather than relying on global availability, e.g.
 * `SBAViewerPage.tsx`/`FdaLetterPageViewer.tsx` and their test files) so this
 * component can mount under the existing runner without a global shim; the
 * browser-gated PROOF that tracking positions correctly is F20's deferred
 * follow-up (rule 24 still SHIPS here — only that proof defers).
 */
function ReportNavigation({ sections }: { sections: TemplatedBodySection[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  // Section anchors repeat across report tabs (every shell tab stays mounted):
  // resolve them inside this tab's pane, never the first match in the document.
  const { rootRef } = useTab();
  const groups = useMemo(() => buildNavGroups(sections), [sections]);
  const sectionIds = useMemo(() => sections.map((section) => section.section_id), [sections]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = sectionIds
      .map((id) => findInTab(rootRef, sectionAnchorId(id)))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) => (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
        setActiveId((topMost.target as HTMLElement).id);
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionIds, rootRef]);

  if (sections.length === 0) return null;

  // fix round 1 (P2-2): the active section's MARKER must stay visible even
  // when its own group is collapsed by the reader — the link that carries it
  // is then hidden inside a closed `<details>`. The containing group's own
  // `<summary>` gets the same active styling, so orientation never depends
  // on which groups happen to be open.
  const activeGroupId = groups.find((group) =>
    group.members.some((member) => sectionAnchorId(member.section_id) === activeId))?.top.section_id ?? null;

  return (
    <nav
      // fix round 1 (P2-2): sticky/independent-scrolling positioning applies
      // at EVERY width, not only `md:` — rule 24 requires reaching any of
      // the 83 sections without scrolling the body, and that holds on a
      // narrow screen too. `md:w-64`/`md:shrink-0` remain width-only
      // (they only matter once the body's `md:flex` puts this beside the
      // content, per rule 26's narrow-viewport stacking). Rule 31: never
      // printed — it is navigation chrome, not report content.
      className="sticky top-4 mb-4 max-h-[calc(100vh-2rem)] space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-sm md:mb-0 md:w-64 md:shrink-0 print:hidden"
      data-testid="report-navigation"
      aria-label="Report section index"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contents</p>
      <ul className="space-y-1" data-testid="report-toc">
        {groups.map((group) => {
          const isActiveGroup = activeGroupId === group.top.section_id;
          return (
            <li key={group.top.section_id}>
              {/* fix round 1 (P2-2): starts OPEN — every one of the 83
                  sections is reachable without an extra click — and stays
                  collapsible (the browser's own uncontrolled `<details>`
                  state), rather than defaulting closed whenever a group
                  actually has children (previously every real top-level
                  group, since every one has subsections). */}
              <details open data-testid={`report-nav-group-${group.top.section_id}`}>
                <summary
                  className={`cursor-pointer font-medium ${isActiveGroup ? 'text-indigo-700 font-semibold' : 'text-slate-800'}`}
                  aria-current={isActiveGroup ? 'true' : undefined}
                >
                  {group.top.number} {group.top.title}
                </summary>
                <ul className="mt-1 space-y-0.5 pl-3">
                  {group.members.map((member) => {
                    const anchor = sectionAnchorId(member.section_id);
                    return (
                      <li key={member.section_id}>
                        <a
                          href={`#${anchor}`}
                          // The browser resolves `#id` to the FIRST match in the
                          // document, which may sit in a hidden tab.
                          onClick={(e) => {
                            const el = findInTab(rootRef, anchor);
                            if (!el) return;
                            e.preventDefault();
                            el.scrollIntoView({ block: 'start' });
                          }}
                          data-testid={`report-toc-link-${member.section_id}`}
                          aria-current={activeId === anchor ? 'true' : undefined}
                          className="block py-0.5 text-slate-600 hover:text-slate-900 aria-[current=true]:font-semibold aria-[current=true]:text-indigo-700"
                        >
                          {member.number} {member.title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </details>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Rule 15's status-line disclosure, relocated to the top by rule 28. `null`
 *  `status_disclosure` means the status needs no such line (`report_writer.
 *  py`'s own decision) — never synthesized here. */
function StatusLine({ status, disclosure }: { status: string; disclosure: string | null | undefined }) {
  if (!disclosure) return null;
  return (
    <p className="text-sm text-amber-900" data-testid="report-status-disclosure">
      Report status: {status}. {disclosure}
    </p>
  );
}

/**
 * Rule 16's coverage statement, relocated to the top of the report by rule
 * 28 — rendered exactly once, here, and never again lower in the body.
 * States two facts SEPARATELY, per rule 16's wording constraint: products
 * selected but never analyzed, and products with unresolved evidence
 * (stating how many of those were analyzed anyway). Never merges them into
 * one "not analyzed" number.
 */
function CoverageStatement({ coverage }: { coverage: AnalysisCoverage | null | undefined }) {
  if (!coverage) return null;
  const { selected_candidates: selected, unresolved_candidates: unresolved, never_analyzed_count: neverAnalyzed, analyzed_and_unresolved_count: analyzedAndUnresolved } = coverage;
  // Only Task 5's rule-16 extras make this statement renderable — an older
  // coverage shape (e.g. the step-progress projection, which carries only
  // the three base counts) says nothing here rather than guess at the two
  // derived facts.
  if (neverAnalyzed === undefined || analyzedAndUnresolved === undefined) return null;
  return (
    <p className="text-sm text-amber-900" data-testid="report-coverage-statement">
      Of {selected} products selected for this cohort, {neverAnalyzed} were never analyzed. Separately,{' '}
      {unresolved} products had unresolved evidence gaps, of which {analyzedAndUnresolved} were analyzed anyway.
    </p>
  );
}

const CONTRACT_STATE_LABELS: Record<string, string> = {
  supported: 'Supported', unknown: 'Unknown', unsupported: 'Unsupported',
  conflicting: 'Conflicting', inapplicable: 'Not applicable',
  not_found: 'Not found after search',
};

// Mirrors backend `CORPUS_DISPLAY_NAMES` (`capability_matrix.py:453-472`),
// except `us_label` reads "US label" here (AC 9 wording) rather than "US
// Labels". Falls back to the raw code for a corpus this map does not know.
const CONTRACT_CORPUS_LABELS: Record<string, string> = {
  us_label: 'US label',
  sba: 'FDA Review',
  fda_letter: 'FDA enforcement letter',
  fda_approval_letter: 'FDA approval letter',
  epar: 'EMA EPAR',
  uk_pil: 'UK PIL',
  ca_pm: 'Product Monograph (Canada)',
  eu: 'EU SmPC',
  eu_history: 'EU historical versions',
  au: 'Australia (TGA)',
  jp_pdf: 'Japan (PMDA) supplementary documents',
  jp_xml: 'Japan (PMDA) package insert',
  draft: 'Draft documents',
};

function contractCorpusLabel(corpus: string): string {
  return CONTRACT_CORPUS_LABELS[corpus] ?? corpus;
}

/** `v{version_number}`, else `submission_type submission_number`, else a date, else the raw anchor. */
function contractSearchVersionLabel(search: import('../types/workstream').ContractSearchReceipt): string {
  const version = search.version;
  if (version.version_number !== undefined) return `v${version.version_number}`;
  if (version.submission_type && version.submission_number) return `${version.submission_type} ${version.submission_number}`;
  return version.review_completion_date ?? version.issued_date ?? version.version_date ?? search.version_anchor;
}

function isContractReportProjection(value: unknown): value is ContractReportProjection {
  if (!value || typeof value !== 'object') return false;
  const projection = value as Record<string, unknown>;
  return Boolean(projection['analysis_contract'] && (projection['tables'] as Record<string, unknown> | undefined)?.['all_records']);
}

function contractValue(value: unknown, field?: import('../types/workstream').AnalysisFieldDefinition): ReactNode {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length === 0 ? <span>(none)</span> : value.map((member) => contractValue(member, field)).join(', ');
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    return field?.allowed_values.find((option) => option.value === value)?.definition ?? value;
  }
  return JSON.stringify(value);
}

function ContractQuoteList({ quotes }: { quotes: { source_key: string; quoted_span: string }[] }) {
  if (quotes.length === 0) return null;
  return <ul className="mt-1 space-y-1 text-xs text-slate-600">{quotes.map((quote, index) => (
    <li key={`${quote.source_key}-${index}`} className="border-l-2 border-slate-200 pl-2">“{quote.quoted_span}”</li>
  ))}</ul>;
}

function ContractCitationList({ excerptIds, citations, fallbackQuotes }: {
  excerptIds: string[];
  citations: Map<string, ExcerptCitation>;
  fallbackQuotes: { source_key: string; quoted_span: string }[];
}) {
  const resolved = excerptIds.flatMap((id) => {
    const citation = citations.get(id);
    return citation ? [citation] : [];
  });
  if (resolved.length === 0) return <ContractQuoteList quotes={fallbackQuotes} />;
  return <ul className="mt-1 space-y-1">{resolved.map((citation) => <CitationRow key={citation.excerpt_id} citation={citation} />)}</ul>;
}

function ContractAnswerView({ envelope, field, citations }: {
  envelope: ContractAnswerEnvelope;
  field?: import('../types/workstream').AnalysisFieldDefinition;
  citations: Map<string, ExcerptCitation>;
}) {
  return (
    <div className="min-w-[10rem] space-y-1" data-answer-state={envelope.state}>
      <span className="text-xs font-semibold text-slate-600">{CONTRACT_STATE_LABELS[envelope.state] ?? envelope.state}</span>
      {envelope.state === 'supported' && <div>{contractValue(envelope.value, field)}</div>}
      {envelope.state === 'not_found' && (envelope.searches ?? []).length > 0 && (
        <ul className="space-y-1 text-xs text-slate-700">
          {(envelope.searches ?? []).map((search, index) => (
            <li key={`${search.corpus}-${search.document_id}-${index}`}>
              No match in {search.title ?? contractCorpusLabel(search.corpus)} ({contractCorpusLabel(search.corpus)}, {contractSearchVersionLabel(search)}) for:{' '}
              {search.terms.map((term) => `"${term}"`).join(', ')}
            </li>
          ))}
        </ul>
      )}
      {envelope.reason && <p className="text-xs text-amber-900">{envelope.reason}</p>}
      <ContractCitationList excerptIds={envelope.excerpt_ids} citations={citations} fallbackQuotes={envelope.source_quotes} />
      {envelope.alternatives.length > 0 && (
        <ul className="space-y-2 text-sm">
          {envelope.alternatives.map((alternative, index) => (
            <li key={index} className="rounded border border-amber-200 bg-amber-50 p-2">
              {contractValue(alternative.value, field)}
              <ContractCitationList excerptIds={alternative.excerpt_ids} citations={citations} fallbackQuotes={alternative.source_quotes} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContractReportBodyView({ body, projection, coverage }: { body: TemplatedReportBody; projection: ContractReportProjection; coverage: AnalysisCoverage | null | undefined }) {
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const contract = projection.analysis_contract;
  const counts = projection.coverage.contract;
  const rows = projection.tables.all_records.rows;
  const products = new Map(rows.map((row) => [row.record_key, row.product_name]));
  const fields = new Map(contract.fields.map((field) => [field.name, field]));
  const stateRows = Object.entries(projection.slots.field_states.fields);
  const citations = new Map((body.citations ?? []).map((citation) => [citation.excerpt_id, citation]));
  for (const row of projection.appendices.evidence.rows) {
    for (const excerpt of row.excerpts) {
      const ref = excerpt.ref;
      if (!citations.has(excerpt.excerpt_id) && ref?.corpus && ref.anchor_kind && ref.binding_time && ref.retrieved_at) {
        citations.set(excerpt.excerpt_id, {
          ...ref,
          excerpt_id: excerpt.excerpt_id,
          corpus: ref.corpus,
          anchor_kind: ref.anchor_kind,
          binding_time: ref.binding_time,
          retrieved_at: ref.retrieved_at,
          quoted_span: ref.quoted_span ?? excerpt.quoted_span,
          deep_link: ref.deep_link ?? excerpt.source_label,
          drug_name: products.get(row.record_key) ?? null,
        });
      }
    }
  }
  const projectionSlots = projectionAddresses(body.projection);
  const placed: CanonicalPlacements = { tables: new Map(), appendices: new Map() };

  return (
    <div className="space-y-5" data-testid="contract-report">
      <h3 className="text-base font-semibold text-slate-900">{body.title}</h3>
      <StatusLine status={body.status} disclosure={body.status_disclosure} />
      <CoverageStatement coverage={coverage} />
      <p className="text-sm text-slate-700">
        {counts.drugs} drugs · {counts.records} records ·{' '}
        <span>{counts.included} included · {counts.excluded} excluded · {counts.undetermined} undetermined</span>
      </p>
      {body.limitations.length > 0 && <ul className="space-y-1" data-testid="report-limitations">{body.limitations.map((limitation, index) => (
        <li key={index} className="text-sm text-amber-900">{limitation}</li>
      ))}</ul>}

      <section className="space-y-2 break-inside-avoid">
        <h4 className={headingClasses(1)}>Research findings</h4>
        {body.slots.map((slot) => (
          <TemplatedSlotBlock key={slot.slot_id} slot={slot} citations={citations} projection={projectionSlots} placed={placed} />
        ))}
        <div className="overflow-x-auto print:overflow-visible">
          <table className="text-sm w-full" data-testid="contract-state-counts">
            <thead><tr><th className="text-left pr-3">Question</th>{['Supported','Unknown','Unsupported','Conflicting','Not applicable','Not found after search'].map((label) => <th key={label} className="text-left pr-3">{label}</th>)}</tr></thead>
            <tbody>{stateRows.map(([name, state]) => <tr key={name} className="break-inside-avoid">
              <td className="pr-3">{fields.get(name)?.label ?? name}</td>
              <td>{state.supported}</td><td>{state.unknown}</td><td>{state.unsupported}</td><td>{state.conflicting}</td><td>{state.inapplicable}</td><td>{state.not_found ?? 0}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h4 className={headingClasses(1)}>{projection.tables.all_records.title}</h4>
        <div className="min-w-0 overflow-x-auto print:overflow-visible">
          <table className="text-sm w-full" data-testid="contract-records-table">
            <thead><tr>
              <th className="text-left pr-4">Drug</th><th className="text-left pr-4">Record dimensions</th><th className="text-left pr-4">Inclusion</th>
              <th className="text-left pr-4">Answers</th>
            </tr></thead>
            <tbody>{rows.map((row) => <tr key={row.record_key} className="align-top break-inside-avoid" data-testid="contract-record-row">
              <td className="pr-4 py-2 font-medium">{row.product_name}</td>
              <td className="pr-4 py-2 space-y-1">
                {contract.record_granularity.dimension_fields.map((name) => <div key={name}><span className="font-medium">{fields.get(name)?.label ?? name}:</span> {contractValue(row.dimensions[name]?.value, fields.get(name))}</div>)}
                {row.identity_refinements.length > 0 && <p className="text-xs text-slate-600">Identity refined from an earlier provisional record.</p>}
              </td>
              <td className="pr-4 py-2"><span className="font-medium capitalize">{row.inclusion.disposition}</span><p className="text-xs text-slate-600">{row.inclusion.reason}</p><ContractCitationList excerptIds={row.inclusion.excerpt_ids} citations={citations} fallbackQuotes={row.inclusion.source_quotes} /></td>
              <td className="pr-4 py-2"><dl className="grid gap-3 sm:grid-cols-2">{contract.fields.map((field) => <div key={field.name} className="break-inside-avoid"><dt className="font-medium text-slate-800">{field.label}</dt><dd><ContractAnswerView envelope={row.fields[field.name]!} field={field} citations={citations} /></dd></div>)}</dl></td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h4 className={headingClasses(1)}>{projection.appendices.evidence.title}</h4>
          <button type="button" className="text-sm text-indigo-700 underline print:hidden" aria-expanded={evidenceExpanded} onClick={() => setEvidenceExpanded((value) => !value)}>
            {evidenceExpanded ? 'Hide evidence' : 'Show evidence'}
          </button>
        </div>
        <div className={`${evidenceExpanded ? 'block' : 'hidden'} print:block space-y-3`} data-testid="contract-evidence-content">
          {projection.appendices.evidence.rows.map((row) => (
            <article key={row.record_key} className="rounded border border-slate-200 p-3 break-inside-avoid">
              <h5 className="font-semibold text-slate-800">{products.get(row.record_key) ?? row.subject_key}</h5>
              <div className="text-sm text-slate-700">{contract.record_granularity.dimension_fields.map((name) => (
                <p key={name}>{fields.get(name)?.label ?? name}: {contractValue(row.dimensions[name]?.value, fields.get(name))}</p>
              ))}</div>
              {row.excerpts.length === 0 ? <p className="text-sm text-amber-900">No admitted excerpt is available for this record.</p> : (
                <ul className="mt-2 space-y-2">{row.excerpts.map((excerpt) => {
                  const citation = citations.get(excerpt.excerpt_id);
                  return citation ? <CitationRow key={excerpt.excerpt_id} citation={citation} /> : <li key={excerpt.excerpt_id}>“{excerpt.quoted_span}”</li>;
                })}</ul>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function TemplatedReportBodyView({ body, coverage }: { body: TemplatedReportBody; coverage: AnalysisCoverage | null | undefined }) {
  const citations = useMemo(
    () => new Map((body.citations ?? []).map((citation) => [citation.excerpt_id, citation])),
    [body.citations],
  );
  const sections = useMemo(() => declaredSections(body), [body]);
  const placed = useMemo(() => placedByBody(body, sections), [body, sections]);
  const projectionSlots = useMemo(() => projectionAddresses(body.projection), [body.projection]);
  const tables: ProjectionTable[] = Object.values(body.projection?.tables ?? {})
    .filter((table) => !placed.tables.has(table.table_id));
  const appendices: ProjectionAppendix[] = Object.values(body.projection?.appendices ?? {})
    .filter((appendix) => !placed.appendices.has(appendix.appendix_id));
  // A slot the writer returned that no declared block placed is kept VISIBLE —
  // the backend deliberately does not drop it, and neither can the last mile.
  const unplaced = body.slots.filter((slot) => !placed.slots.has(slot.slot_id));
  // Read-time `drug:<id>` display names, shared by every computed slot's
  // drill-down. Absent on a report assembled before this landed, and on one
  // whose subjects resolved to no public product — the keys render as
  // themselves then.
  const subjectNames = useMemo(
    () => (body as { subject_display?: Record<string, string> }).subject_display ?? {},
    [body],
  );

  return (
    // Rule 24: the nav sits beside the body on a wide screen and stacks above
    // it on a narrow one (rule 26's declared 390px case) — `min-w-0` on the
    // content column so a long cell cannot force the FLEX ITEM (and so the
    // page) wider than the viewport; only the table's own container scrolls.
    <SubjectNamesContext.Provider value={subjectNames}>
    <div className="templated-report md:flex md:items-start md:gap-6" data-testid="templated-report">
      <ReportNavigation sections={sections} />
      <div className="min-w-0 flex-1 space-y-4" data-testid="report-body-main">
        <h3 className="text-base font-semibold text-slate-900" data-testid="report-title">{body.title}</h3>

        {/* Rule 28: these three render near the TOP, before any body section
            — a reader must not have to reach page 170 to learn the report's
            coverage is partial. `limitations` moves here from the bottom;
            rule 16's coverage statement renders exactly once, here, never
            again lower in the body. */}
        <StatusLine status={body.status} disclosure={body.status_disclosure} />
        <CoverageStatement coverage={coverage} />
        {(body.limitations ?? []).length > 0 && (
          <ul className="space-y-0.5" data-testid="report-limitations">
            {body.limitations.map((limitation, index) => (
              <li key={index} className="text-sm text-amber-900">{limitation}</li>
            ))}
          </ul>
        )}

        {sections.map((section) => {
          const Heading = `h${Math.min(6, 3 + Math.max(1, section.level))}` as 'h4' | 'h5' | 'h6';
          const blocks = (subject: TemplatedSubject | null) => section.blocks.map((block, index) => (
            <SectionBlockView
              key={`${block.kind}:${block.value}:${index}`}
              block={block}
              subject={subject}
              body={body}
              citations={citations}
              projectionSlots={projectionSlots}
              placed={placed}
            />
          ));
          // Rule 4: a repeating section's subjects with an unsupported identity
          // are dropped in full — no field lines, no prose, nothing rendered —
          // before the block list ever runs for them.
          const keptSubjects = section.repeat
            ? section.subjects.filter((subject) => subjectIdentitySupported(subject))
            : section.subjects;
          const omissions = section.blocks.length > 0 ? computeSectionOmissions(section, body, placed.appendices) : null;
          return (
            <section
              key={section.section_id}
              id={sectionAnchorId(section.section_id)}
              data-testid={`report-section-${section.section_id}`}
              // `scroll-mt-4`: a jump to this anchor lands just below the top
              // of the viewport rather than flush against it.
              className="space-y-1 scroll-mt-4"
            >
              {/* Rule 27: heading size/weight carries the template's own
                  level (1-6), independent of the semantic `h4`-`h6` tag. */}
              <Heading className={headingClasses(section.level)}>{section.number} {section.title}</Heading>
              {section.blocks.length === 0
                ? body.slots
                  .filter((slot) => slot.section_id === section.section_id)
                  .map((slot) => (
                    <TemplatedSlotBlock key={slot.slot_id} slot={slot} citations={citations} projection={projectionSlots} placed={placed} />
                  ))
                : section.repeat
                  ? keptSubjects.map((subject) => (
                    <div
                      key={subject.subject_key}
                      className="space-y-1"
                      data-testid={`report-subject-${section.section_id}-${subject.subject_key}`}
                    >
                      {blocks(subject)}
                    </div>
                  ))
                  : blocks(null)}
              {/* Rule 8: never deleted — a section whose every block was omitted
                  still renders its heading and this note, above. */}
              {omissions && <SectionOmissionNote omissions={omissions} />}
            </section>
          );
        })}

        {unplaced.length > 0 && (
          <section data-testid="report-unplaced-slots" className="space-y-1">
            <h4 className={headingClasses(4)}>Content outside the template’s sections</h4>
            {unplaced.map((slot) => (
              <TemplatedSlotBlock key={slot.slot_id} slot={slot} citations={citations} projection={projectionSlots} placed={placed} />
            ))}
          </section>
        )}

        {tables.length > 0 && (
          <section className="space-y-3" data-testid="report-tables">
            {tables.map((table) => (
              <ProjectionTableView
                key={table.table_id}
                testId={`report-table-${table.table_id}`}
                title={table.title}
                columns={table.columns}
                rows={table.rows.map((row) => ({ slotId: row.slot_id, cells: row.cells }))}
              />
            ))}
          </section>
        )}

        {appendices.length > 0 && (
          <section className="space-y-3" data-testid="report-appendices">
            {appendices.map((appendix) => (
              <AppendixView
                key={appendix.appendix_id}
                appendix={appendix}
                testId={`report-appendix-${appendix.appendix_id}`}
              />
            ))}
            {/* Rule 8: these appendices are outside every numbered section, so
                `SectionOmissionNote` never runs for them — this is their only
                disclosure surface. */}
            <TrailingAppendixNote appendices={appendices} />
          </section>
        )}
      </div>
    </div>
    </SubjectNamesContext.Provider>
  );
}

export function ReportView({ report }: { report: WorkstreamReport }) {
  const templated = templatedBody(report.synthesis);
  return (
    <div className="space-y-4" data-testid="report">
      <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-slate-900">Report</h2>
          <span className="text-xs text-slate-600" data-testid="report-outcome">
            Outcome: {statusLabel(report.workstream_status, report.synthesis_status)}
          </span>
        </div>

        {report.synthesis_status === 'unavailable' ? (
          <p className="text-sm text-amber-800" data-testid="synthesis-unavailable">
            {report.synthesis?.unavailable_reason || report.synthesis?.summary || 'Synthesis is unavailable.'}{' '}
            Exact step outputs remain available in the steps above.
          </p>
        ) : templated ? (
          isContractReportProjection(templated.projection)
            ? <ContractReportBodyView body={templated} projection={templated.projection} coverage={report.analysis_coverage} />
            : <TemplatedReportBodyView body={templated} coverage={report.analysis_coverage} />
        ) : (
          <ReportText text={report.synthesis?.summary ?? ''} testId="synthesis-summary" />
        )}

        {(report.synthesis?.claims ?? []).length > 0 && (
          <><h3 className="text-sm font-semibold text-slate-900">Cited findings</h3><ul className="space-y-2">
            {(report.synthesis?.claims ?? []).map((c, i) => (
              <li key={i}>
                <div className="text-sm text-slate-800">
                  <ReportText text={c.text} />
                  {!c.supported && (
                    <span className="text-amber-900 font-medium" data-testid="unsupported-claim">
                      (unsupported{c.flag_reason ? `: ${c.flag_reason}` : ''})
                    </span>
                  )}
                </div>
                {c.citations.length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {c.citations.map((cit) => (
                      <CitationRow key={cit.ref_key} citation={cit} />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul></>
        )}

      </section>


    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// The plan "coming together" reveal is a first-impression flourish: it should
// play ONCE per workstream, ever — not after an edit→save (new plan version),
// not on a re-view, and not on a hard refresh. Persist a per-workstream flag in
// localStorage so that knowledge survives remounts and reloads. (Wrapped in
// try/catch: localStorage can throw in private-mode / storage-disabled.)
function planRevealKey(id: string | null): string | null {
  return id ? `cepi:planRevealed:${id}` : null;
}
function planAlreadyRevealed(id: string | null): boolean {
  const key = planRevealKey(id);
  if (!key) return false;
  try { return localStorage.getItem(key) !== null; } catch { return false; }
}
function markPlanRevealed(id: string | null): void {
  const key = planRevealKey(id);
  if (!key) return;
  try { localStorage.setItem(key, '1'); } catch { /* storage unavailable — reveal may replay, harmless */ }
}

export default function WorkstreamPage() {
  const { id } = useParams<{ id: string }>();
  const workstreamId = id ?? null;
  // Rule 29b's explicit expected-plan-version identifier, ridden on the URL
  // (`?plan_version=N`, controller ruling R-C). Read directly from the URL —
  // never inferred from `detail`/`approvedPlanVersion` — because an echoed
  // argument is not provenance (rule 29b).
  const [searchParams] = useSearchParams();
  const requestedVersionParam = searchParams.get('plan_version');
  const requestedVersion = requestedVersionParam !== null && requestedVersionParam.trim() !== '' && Number.isFinite(Number(requestedVersionParam))
    ? Number(requestedVersionParam)
    : null;

  const [detail, setDetail] = useState<WorkstreamDetail | null>(null);
  const [plan, setPlan] = useState<PlanVersionResponse | null>(null);
  // False until the plan has finished appearing. Keeps the thinking agent in
  // "writing" mode while the steps stagger in, flipping to "complete" only after
  // they have all shown up. Initialised from the persisted "already revealed"
  // flag so a refresh of an already-seen plan starts in the completed state (no
  // "writing" flash, no replay).
  const [planReady, setPlanReady] = useState<boolean>(() => planAlreadyRevealed(workstreamId));
  const [report, setReport] = useState<WorkstreamReport | null>(null);
  // Rule 29d: the requested version's report is unavailable (superseded,
  // cleared, or never existed). Holds the version number that failed so the
  // banner can name it; null means no versioned request has failed.
  const [versionUnavailable, setVersionUnavailable] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<Pick<WorkstreamDetail, 'id' | 'status' | 'cancel_requested'> | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [outputStates, setOutputStates] = useState<Record<string, ToolAuditState>>({});
  const [capabilityCatalog, setCapabilityCatalog] = useState<CapabilityCatalog | null>(null);
  const [capabilityCatalogFailed, setCapabilityCatalogFailed] = useState(false);
  const auditRequests = useRef(new Map<string, Promise<void>>());
  const currentOutputKeys = useRef<Record<number, string>>({});
  const capabilityRequest = useRef<Promise<void> | null>(null);
  // Fix round 1 (P1): the sequence number of the most recently ISSUED report
  // fetch. A response is applied to state only when it is still the latest
  // one in flight — otherwise a late-arriving response for a SUPERSEDED
  // identity (e.g. a slow request for v2 resolving after the reader already
  // navigated to v999 and got its 409) can resurrect stale content, or wipe
  // out a correct "unavailable" state, well after a newer response already
  // settled things correctly. See `loadReport`.
  const reportRequestSeq = useRef(0);
  // Durable, NOT in-flight-only (spec §4.4): the plan GET 404s when no plan
  // exists yet (agent_workstream_routes.py:869), which leaves `plan` at null
  // after a failed attempt. An in-flight-only guard (e.g. a boolean cleared in
  // a `finally`) would therefore refetch on every RE-RUN of the effect once
  // settled (a re-run needing a real dependency change — React skips an
  // effect whose deps are unchanged, so a plain rerender alone won't do it).
  const planFetchAttempted = useRef<Set<string>>(new Set());
  const stream = useWorkstreamStream(workstreamId);

  const loadDetail = useCallback(async () => {
    if (!workstreamId) return;
    const res = await apiGet<WorkstreamDetail>(`/v2/workstreams/${workstreamId}`);
    if (!res.ok || !res.body) {
      setLoadError(
        res.status === 404
          ? 'This agent run is not available.'
          : `Could not load this workstream (${res.status}).`,
      );
      return;
    }
    setLoadError(null);
    setDetail(res.body);
  }, [workstreamId]);

  const loadPlan = useCallback(async (version?: number) => {
    if (!workstreamId) return;
    const qs = version ? `?version=${version}` : '';
    const res = await apiGet<PlanVersionResponse>(`/v2/workstreams/${workstreamId}/plan${qs}`);
    if (res.ok && res.body) setPlan(res.body);
  }, [workstreamId]);

  const loadCapabilityCatalog = useCallback(async () => {
    if (capabilityCatalog || capabilityRequest.current) {
      await capabilityRequest.current;
      return;
    }
    const request = apiGet<CapabilityCatalog>('/v2/workstreams/capabilities').then((res) => {
      if (res.ok && res.body) {
        setCapabilityCatalog(res.body);
        setCapabilityCatalogFailed(false);
      } else {
        setCapabilityCatalogFailed(true);
      }
    });
    capabilityRequest.current = request;
    try { await request; } finally { capabilityRequest.current = null; }
  }, [capabilityCatalog]);

  // `version` carries rule 29b's explicit expected-plan-version identifier
  // (the `plan_version` URL param). Omitted, behaviour is unchanged: the
  // current approved version's report, and a 409 is just the normal
  // pre-approval state. Supplied, the route validates the requested version's
  // report provenance and either renders it or answers unavailable (rule
  // 29d) — and on ANY non-ok response for an explicit version, the stale
  // report already on screen must be cleared rather than left standing under
  // a non-matching identifier (rule 29d: "the current report must never be
  // rendered under a non-matching identifier").
  const loadReport = useCallback(async (version?: number | null) => {
    if (!workstreamId) return;
    const seq = ++reportRequestSeq.current;
    const qs = version != null ? `?plan_version=${version}` : '';
    const res = await apiGet<WorkstreamReport>(`/v2/workstreams/${workstreamId}/report${qs}`);
    // Fix round 1 (P1): discard a response that is no longer the latest
    // issued request — an interleaved navigation may have already started a
    // NEWER request (and possibly already rendered its own outcome) by the
    // time this one resolves. Applying a stale response here — success OR
    // failure — would either resurrect a superseded version's content or
    // silently clear a correct "unavailable" state for the identifier the
    // reader is now actually looking at. This check must run before ANY
    // state write below.
    if (seq !== reportRequestSeq.current) return;
    if (res.ok && res.body) {
      setReport(res.body);
      setVersionUnavailable(null);
      return;
    }
    if (version != null) {
      setReport(null);
      setVersionUnavailable(version);
    }
  }, [workstreamId]);

  useEffect(() => {
    void loadDetail();
    void loadPlan();
  }, [loadDetail, loadPlan]);

  // Re-sync the reveal gate only when the WORKSTREAM changes (not on every plan
  // version): a workstream whose plan was already revealed stays "complete" and
  // never re-animates; a fresh, never-seen workstream arms the reveal.
  useEffect(() => {
    setPlanReady(planAlreadyRevealed(workstreamId));
  }, [workstreamId]);

  // Persist "revealed" as soon as the plan first LOADS — not only when the
  // staged reveal finishes (~15-20s later). Otherwise a refresh mid-reveal
  // finds no flag and replays the animation. This session's reveal still plays
  // (its `animate` was fixed at mount from the pre-load flag state); the flag
  // only governs FUTURE mounts, which then open instantly.
  useEffect(() => {
    if (plan) markPlanRevealed(workstreamId);
  }, [plan, workstreamId]);

  // The snapshot is persisted state, so it — not an in-memory accumulation —
  // is what a reload or a reconnect reconstructs from (spec §6.2).
  // A direct cancellation can finish before the next SSE snapshot arrives.
  const currentCancel = cancelResult?.id === workstreamId ? cancelResult : null;
  const liveStatus = currentCancel && TERMINAL_WORKSTREAM_STATUSES.has(currentCancel.status)
    ? currentCancel.status
    : stream.snapshot?.status ?? detail?.status ?? null;
  const cancelRequested = Boolean(currentCancel?.cancel_requested || stream.snapshot?.cancel_requested || detail?.cancel_requested);
  const actionRequiredReason = (
    stream.snapshot?.action_required_reason ?? detail?.action_required_reason ?? null
  );
  const steps: StepSummary[] = stream.snapshot?.steps ?? detail?.steps ?? [];
  const approvedPlanVersion = stream.snapshot?.approved_plan_version ?? detail?.approved_plan_version ?? null;
  // Fix round 1 (P1), defense in depth: every render-time check of `report`'s
  // identity must agree with the URL's explicit `plan_version` when one is
  // present — not only with the current approval. `approvedPlanVersion` alone
  // is not a sufficient gate: a successful versioned fetch's echoed
  // `plan_version` is ALWAYS equal to the current approval by construction
  // (the backend only ever holds one stored payload, for the current
  // approval), so that comparison provides no protection at all against
  // showing a report that belongs to a DIFFERENT identifier than the one on
  // screen — the fetch-sequencing fix in `loadReport` is the only thing that
  // would otherwise stand between a stale response and the screen. Falls back
  // to `approvedPlanVersion` when no version is named in the URL, preserving
  // the app's own (unversioned) fetch behaviour exactly as before.
  const expectedReportVersion = requestedVersion ?? approvedPlanVersion;
  const synthesisStatus = report?.plan_version === expectedReportVersion
    ? report.synthesis_status
    : stream.snapshot?.synthesis_status ?? detail?.synthesis_status;
  const outputKeys: Record<number, string> = Object.fromEntries(steps.map((s) => [s.ordinal, JSON.stringify([workstreamId, approvedPlanVersion, s.ordinal, s.attempt, s.completed_at])]));
  currentOutputKeys.current = outputKeys;

  useEffect(() => {
    const planNeedsCatalog = plan?.content.steps.some((step) => Array.isArray(step.scope['document_types']));
    const reportNeedsCatalog = report?.sections.some((section) => Array.isArray(section.scope?.['document_types']));
    if (!planNeedsCatalog && !reportNeedsCatalog) return;
    void loadCapabilityCatalog();
  }, [loadCapabilityCatalog, plan, report]);

  useEffect(() => {
    if (liveStatus && TERMINAL_WORKSTREAM_STATUSES.has(liveStatus)) {
      // Re-request whatever identifier the reader is looking at — the current
      // approved version when no `plan_version` param is present, or the
      // URL's explicit version otherwise (rule 29b).
      void loadReport(requestedVersion);
      void loadDetail();
    }
  }, [liveStatus, loadReport, loadDetail, requestedVersion]);

  // Belt-and-suspenders refresh while the run is active. Live SSE (now over the
  // Gen 2 streaming proxy) plus its auto-reconnect already keep the page current;
  // this poll is the safety net for a silently stalled stream or an SSE that
  // errored out and stopped reconnecting (401/404) — cases where the snapshot
  // would otherwise freeze until a manual reload. Refreshes `detail` only (never
  // `loadPlan`), so a plan version the owner is viewing while awaiting approval
  // is never snapped back underneath them; when the snapshot is absent `detail`
  // is what drives `liveStatus`, so this also unsticks that path. Stops at any
  // terminal status.
  useEffect(() => {
    if (!liveStatus || TERMINAL_WORKSTREAM_STATUSES.has(liveStatus)) return;
    const interval = setInterval(() => { void loadDetail(); }, 30_000);
    return () => clearInterval(interval);
  }, [liveStatus, loadDetail]);

  // Spec §4.4 — the plan appears without a manual reload.
  //
  // STATE-based, not transition-based. The mount fetch and the SSE connection
  // are independent, so a snapshot already reading `awaiting_approval` can
  // arrive first: liveStatus goes null -> awaiting_approval and never "leaves"
  // drafting. A guard requiring an OBSERVED prior `drafting` misses that race;
  // keying off the current state is correct under every ordering.
  //
  // `awaiting_approval` is the only status that NEEDS this trigger: it is where a
  // newly planned workstream lands, plan insertion commits together with that status
  // (plan_service.py:795, :815-823), and every later status is reached only through
  // it. (`showPlanPanel` also admits `drafting`, which only matters once a plan is
  // already loaded.)
  useEffect(() => {
    if (!workstreamId) return;
    if (liveStatus !== 'awaiting_approval') return;
    if (plan !== null) return;
    const key = `${workstreamId}:awaiting_approval`;
    if (planFetchAttempted.current.has(key)) return;
    planFetchAttempted.current.add(key);
    void loadPlan();
    void loadDetail();
  }, [workstreamId, liveStatus, plan, loadPlan, loadDetail]);

  // Gated on `report_generated_at`, NOT on `approved_plan_version`: approval
  // happens the moment execution STARTS, so keying off it fetched a report for
  // a workstream that had not run yet. The backend answers with a partial
  // report (legitimately — a terminal-but-incomplete run still has one), so the
  // page rendered a second copy of every step under a "Report / Outcome:
  // Running" heading, each marked "Not started" and carrying the orange
  // "No search scope was recorded for this step" caveat. That caveat is
  // truthful for a step that FAILED without recording scope, and actively
  // misleading for one that simply has not started.
  //
  // `report_generated_at` is null until synthesis writes the report, which is
  // exactly the condition under which there is something worth showing. The
  // terminal-status effect above still covers a run that finishes while the
  // page is open.
  //
  // Only for the UNVERSIONED (current) fetch: when the URL names an explicit
  // `plan_version`, the dedicated effect below owns that request instead —
  // approval-clearing sets `report_generated_at` to NULL (`plan_service.py`),
  // which is exactly rule 29d's cleared-report negative path, so gating a
  // versioned request on it would make the backend's 409 unreachable and
  // leave the reader looking at a blank pane instead of a message.
  useEffect(() => {
    if (requestedVersion != null) return;
    if (detail?.report_generated_at) void loadReport();
  }, [detail?.report_generated_at, loadReport, requestedVersion]);

  // Rule 29 (Task 9b): a URL naming an explicit `plan_version` must reach the
  // route's provenance guard regardless of `report_generated_at` — that gate
  // exists only for the convenience "current report" fetch above. Fires on
  // every change of `workstreamId` or `requestedVersion`, so navigating from
  // one versioned link to another (e.g. a successful v2 view to an
  // unavailable v1/999 URL) issues a fresh request rather than reusing a
  // stale one, and `loadReport` clears any stale `report` on a non-ok
  // response so nothing superseded is ever left on screen.
  useEffect(() => {
    if (requestedVersion == null) return;
    void loadReport(requestedVersion);
  }, [requestedVersion, loadReport]);

  const handleSaveEdit = useCallback(async (content: PlanContent) => {
    if (!workstreamId) return;
    setBusy(true);
    setConflict(null);
    const res = await apiPost<PlanVersionResponse>(
      `/v2/workstreams/${workstreamId}/plan`,
      { content },
    );
    setBusy(false);
    if (res.ok && res.body) {
      setPlan(res.body);
      void loadDetail();
    } else {
      setConflict(`Your edit was not saved (${res.status}).`);
    }
  }, [workstreamId, loadDetail]);

  const handleRevise = useCallback(async (instruction: string) => {
    if (!workstreamId) return 'This workstream is not available.';
    setBusy(true);
    setConflict(null);
    const res = await apiPost<PlanVersionResponse | { detail: string }>(
      `/v2/workstreams/${workstreamId}/plan/revise`,
      { instruction },
    );
    setBusy(false);
    if (res.ok && res.body && 'workstream_id' in res.body) {
      setPlan(res.body);
      void loadDetail();
      return null;
    } else {
      const detail = res.body && typeof res.body === 'object' && 'detail' in res.body
        ? res.body.detail
        : `Planner revision failed (${res.status}).`;
      return detail;
    }
  }, [workstreamId, loadDetail]);

  const handleApprove = useCallback(async (version: number, contentHash: string) => {
    if (!workstreamId) return;
    setBusy(true);
    setConflict(null);
    // BOTH fields travel: the version on screen and the hash of the content on
    // screen (spec §3.3). A 409 means the plan moved under us — we surface it
    // and force a re-read. We never silently retry, and never re-approve with a
    // hash the user did not see.
    const res = await apiPost<ApprovePlanResponse>(
      `/v2/workstreams/${workstreamId}/approve`,
      { version, content_hash: contentHash },
    );
    setBusy(false);
    if (res.status === 409) {
      setConflict(
        'A newer plan version was saved after this one was shown to you, so the ' +
        'approval was refused.',
      );
      await loadPlan();
      await loadDetail();
      return;
    }
    if (!res.ok) {
      setConflict(`Approval failed (${res.status}). Nothing was approved.`);
      return;
    }
    await loadPlan();
    await loadDetail();
  }, [workstreamId, loadPlan, loadDetail]);

  const handleSelectVersion = useCallback((version: number) => {
    void loadPlan(version);
  }, [loadPlan]);

  const handleCancel = async () => {
    if (!workstreamId || !detail?.is_owner || busy || cancelBusy || cancelRequested ||
        !liveStatus || TERMINAL_WORKSTREAM_STATUSES.has(liveStatus)) return;
    if (!window.confirm('Cancel this run? Any work in progress will stop.')) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      const res = await apiPost<Pick<WorkstreamDetail, 'id' | 'status' | 'cancel_requested'>>(
        `/v2/workstreams/${workstreamId}/cancel`, {},
      );
      if (!res.ok || !res.body) {
        setCancelError('Could not cancel this run. Please try again.');
        return;
      }
      setCancelResult(res.body);
    } catch {
      setCancelError('Could not cancel this run. Please try again.');
    } finally {
      setCancelBusy(false);
    }
  };

  const loadToolAudit = useCallback(async (ordinal: number, candidatesOnly = false, retry = true) => {
    if (!workstreamId) return;
    const publicationKey = outputKeys[ordinal];
    if (!publicationKey) return;
    const key = `${publicationKey}:${candidatesOnly ? 'candidates' : 'audit'}`;
    if (outputStates[key] && (!retry || outputStates[key]?.kind !== 'error')) return;
    if (auditRequests.current.has(key)) return;
    setOutputStates((current) => ({ ...current, [key]: { kind: 'loading' } }));
    const request = (async () => {
      const res = await apiGet<StepToolCallsResponse>(
        `/v2/workstreams/${workstreamId}/steps/${ordinal}/tool-calls?${candidatesOnly ? 'candidates_only=true' : 'include_history=true'}`,
      );
      const data = res.body;
      if (currentOutputKeys.current[ordinal] !== publicationKey) return;
      const expectedAttempt = steps.find((step) => step.ordinal === ordinal)?.attempt;
      const valid = res.ok && data !== null && data.workstream_id === workstreamId && data.step_ordinal === ordinal && data.approved_plan_version === approvedPlanVersion && (data.step_attempt === undefined || expectedAttempt === undefined || data.step_attempt === expectedAttempt);
      const next: ToolAuditState = valid
        ? { kind: 'loaded', data: data as StepToolCallsResponse }
        : { kind: 'error', status: res.ok ? 0 : res.status };
      setOutputStates((current) => ({
        ...current,
        [key]: next,
      }));
    })();
    auditRequests.current.set(key, request);
    try { await request; } finally { auditRequests.current.delete(key); }
  }, [outputStates, workstreamId, outputKeys, approvedPlanVersion, steps]);

  useEffect(() => {
    if (approvedPlanVersion === null) return;
    for (const step of steps) {
      if (step.status === 'done' || step.status === 'flagged' || step.status === 'failed') {
        void loadToolAudit(step.ordinal, true, false);
      }
    }
  }, [approvedPlanVersion, steps, loadToolAudit]);

  // A step's candidate output is published a moment AFTER its status flips to
  // terminal, so the immediate fetch above can race and land an `error` that
  // (retry=false) never clears — the candidates then only appeared after a
  // manual reload. Schedule a couple of delayed retries per completed step so
  // they show up on their own once the backend has published. `loadToolAudit`
  // with retry=true re-fetches ONLY an error/missing state (a successful load
  // is a no-op), and the ref guards against scheduling the same retry twice.
  const candidateRetryRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (approvedPlanVersion === null) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const step of steps) {
      if (step.status !== 'done' && step.status !== 'flagged' && step.status !== 'failed') continue;
      const pk = outputKeys[step.ordinal];
      if (!pk) continue;
      for (const delay of [3000, 9000]) {
        const guard = `${pk}@${delay}`;
        if (candidateRetryRef.current.has(guard)) continue;
        candidateRetryRef.current.add(guard);
        timers.push(setTimeout(() => { void loadToolAudit(step.ordinal, true, true); }, delay));
      }
    }
    return () => timers.forEach(clearTimeout);
  }, [approvedPlanVersion, steps, outputKeys, loadToolAudit]);

  const toolAudits: Record<string, ToolAuditState> = {};
  const candidateStates: Record<string, ToolAuditState> = {};
  for (const step of steps) {
    const visibleKey = `${workstreamId}:${step.ordinal}`;
    const audit = outputStates[`${outputKeys[step.ordinal]}:audit`];
    const candidates = outputStates[`${outputKeys[step.ordinal]}:candidates`];
    if (audit) toolAudits[visibleKey] = audit;
    if (candidates) candidateStates[visibleKey] = candidates;
  }

  const showPlanPanel = useMemo(
    () => plan !== null && (liveStatus === 'awaiting_approval' || liveStatus === 'drafting'),
    [plan, liveStatus],
  );

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm text-slate-800" role="alert">{loadError}</p>
          <Link to="/?mode=workstream" className="text-sm text-indigo-700 underline">
            Back to workstreams
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <header className="space-y-2 border-b border-slate-200 pb-4">
          <Link to="/?mode=workstream" className="text-sm text-indigo-700 underline">
            Back to workstreams
          </Link>
          <h1 className="text-xl font-semibold leading-snug text-slate-900 break-words sm:text-2xl">
            {detail?.request_text ?? stream.snapshot?.request_text ?? 'Workstream'}
          </h1>
          <p className="text-sm text-slate-600" data-testid="workstream-status">
            {liveStatus ? statusLabel(liveStatus, synthesisStatus) : 'Loading…'}
            {cancelRequested && liveStatus && !TERMINAL_WORKSTREAM_STATUSES.has(liveStatus)
              ? ' · stopping'
              : ''}
          </p>
          {/* The "Currently on step N" header line was removed — the thinking
              agent (PlanningIndicator, below) is now the single place that
              announces the running step ("Currently running Step N."). */}
          {detail?.is_owner && liveStatus && !TERMINAL_WORKSTREAM_STATUSES.has(liveStatus) && (
            <button type="button" onClick={() => void handleCancel()}
              disabled={busy || cancelBusy || cancelRequested}
              className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50">
              {cancelBusy ? 'Cancelling…' : cancelRequested ? 'Stopping…' : 'Cancel run'}
            </button>
          )}
          {cancelError && <p role="alert" className="text-sm text-red-800">{cancelError}</p>}
          {stream.connection === 'reconnecting' && (
            <p className="text-xs text-slate-500" data-testid="stream-reconnecting">
              Reconnecting — progress is restored from saved state, not replayed.
            </p>
          )}
          {detail?.infeasible_reason && (
            <p className="text-sm text-amber-900" data-testid="infeasible-reason">
              {detail.infeasible_reason}
            </p>
          )}
          {liveStatus === 'awaiting_approval' && actionRequiredReason && (
            <p data-testid="action-required-reason" className="text-sm text-amber-900">
              {actionRequiredReason.message}
            </p>
          )}
          {/* Rule 29/30: an ordinary authenticated, org-scoped link to THIS
              report — the version currently on screen, carried explicitly so
              a later re-run or a superseding approval cannot silently swap
              in different content under the same URL a recipient already
              has (rule 29d). No token, no new endpoint, no expiry. Opens in
              a new tab because it is meant to be handed to a same-org
              colleague, not to navigate the sender away.
              Fix round 1 (P1): also gated on `expectedReportVersion` — while
              a navigation's report request is still pending, `report` state
              still holds the PREVIOUS identifier's payload, and this link
              must not keep advertising that stale version as "this report"
              once the URL has moved on. */}
          {report && workstreamId && report.plan_version === expectedReportVersion && (
            <a
              href={`/workstream/${workstreamId}/report?plan_version=${report.plan_version}`}
              onClick={(e) => openInAppLink(e, `/workstream/${workstreamId}/report?plan_version=${report.plan_version}`)}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm text-indigo-700 underline underline-offset-2"
              data-testid="report-version-link"
            >
              Open this report version in a new tab
            </a>
          )}
          {versionUnavailable !== null && (
            <p role="alert" className="text-sm text-amber-900" data-testid="report-version-unavailable">
              The report for the requested version ({versionUnavailable}) is unavailable.
            </p>
          )}
        </header>

        {/* The "thinking agent" now persists across every phase — it self-gates
            (renders nothing for failed/cancelled/infeasible/queued) and shows the
            phase-appropriate line: drafting narration, "draft plan complete —
            please review", "Currently running Step N", or "report ready for
            review". */}
        {liveStatus && (
          <PlanningIndicator
            events={stream.events}
            status={liveStatus}
            currentStepOrdinal={steps.find((s) => displayStatusOf(s, liveStatus) === 'running')?.ordinal ?? null}
            planReady={planReady}
          />
        )}

        {showPlanPanel && plan && (
          <PlanPanel
            plan={plan}
            isOwner={detail?.is_owner ?? false}
            busy={busy || cancelBusy || cancelRequested}
            conflict={conflict}
            onSaveEdit={handleSaveEdit}
            onRevise={handleRevise}
            onApprove={handleApprove}
            onSelectVersion={handleSelectVersion}
            catalog={capabilityCatalog}
            catalogFailed={capabilityCatalogFailed}
            onLoadCatalog={loadCapabilityCatalog}
            onPlanRevealed={() => { markPlanRevealed(workstreamId); setPlanReady(true); }}
            animatePlanReveal={!planReady}
          />
        )}

        {!showPlanPanel && steps.length > 0 && (
          <section className="space-y-3" data-testid="progress-panel">
            <h2 className="text-lg font-semibold text-slate-900">Progress</h2>
            <StepRail steps={steps} workstreamStatus={liveStatus} renderStep={(s) => (
              <StepProgressRow key={outputKeys[s.ordinal]} step={s} workstreamStatus={liveStatus} report={s.kind === 'analysis' && report?.plan_version === expectedReportVersion ? report : null} {...(workstreamId && toolAudits[`${workstreamId}:${s.ordinal}`] ? { auditState: toolAudits[`${workstreamId}:${s.ordinal}`] } : {})} candidateState={candidateStates[`${workstreamId}:${s.ordinal}`]} {...(capabilityCatalog?.ha_document_types ? { haDocumentTypes: capabilityCatalog.ha_document_types } : {})} onOpenAudit={(candidatesOnly) => void loadToolAudit(s.ordinal, candidatesOnly)} />
            )} />
          </section>
        )}

        {report && report.plan_version === expectedReportVersion && report.contract !== 'cohort_analysis_v1' && !steps.some((step) => step.kind === 'analysis') && <ReportView key={`${workstreamId}:${approvedPlanVersion}`} report={report} />}
      </div>
    </div>
  );
}
